import React, { useMemo, useState, useEffect } from "react";
import styles from "./Dashboard.module.scss";
import { readDashboardFilters, saveDashboardFilters } from "./dashboardFilters";
import useGuestCheckoutRedirect from "./useGuestCheckoutRedirect";
import useInteractionHistory, { InteractionLog, parseInteractionLog } from "./useInteractionHistory";
import QuickViewButton from "./QuickViewButton";
import { ConsentStatusPill, NextAppointmentPill, TreatmentValuePill } from "./DashboardPills";
import { DashboardSort, getConsentDisplayStatus, isArchivedRecord, loadDashboardPage } from "./dashboardPresentation";
import { ClearButton } from "../../common/ClearControls";
import AiBetaBadge from "../../common/AiBetaBadge";
import DashboardFilterSelect from "./DashboardFilterSelect";
import TranscriptionFilters from "./TranscriptionFilters";
import {
  FiMonitor,
  FiClock,
  FiMail,
  FiSearch,
  FiGrid,
  FiCalendar,
  FiSend,
  FiArchive,
  FiDownload,
  FiRotateCcw,
  FiInfo,
  FiArrowUp,
  FiArrowDown,
  FiCpu,
  FiActivity,
  FiMic,
  FiFileText,
  FiAlignLeft,
  FiEdit3,
  FiSliders,
  FiUpload,
  FiUser,
} from "react-icons/fi";
import Header from "../Header/header";
import QrModal from "../Dashboard/QrModal";
import axios from "axios";
import DashboardTutorialOverlay, {
  DashboardTutorialStep,
} from "./DashboardTutorialOverlay";
import Tooltip from "@mui/material/Tooltip";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardConfirmationDialog from "./DashboardConfirmationDialog";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import Lottie from "lottie-react";
import bulkDownloadAnim from "./animations/bulkDownloading.json";
import { FiEye } from "react-icons/fi";
import { FiAlertTriangle } from "react-icons/fi";
import { MdAutoAwesome } from "react-icons/md";

// ---------- Types ----------
type ConsentStatus = "EMAIL_BOUNCED" | "NOT_SIGNED" | "SIGNED";
type TabKey = "all" | "today" | "reminders" | "upload";
type DashboardPatientOption = { id: number; label: string; raw: Record<string, any> };
const WAITING_ROOM_SECTIONS = {
  all: { title: "All appointments", label: "All", icon: FiGrid },
  today: { title: "Today's appointments", label: "Due today", icon: FiClock },
  reminders: { title: "Future appointments", label: "Send reminder", icon: FiSend },
  upload: { title: "Ready for upload", label: "Ready for upload", icon: FiUpload },
};
type Appointment = {
  id: string;
  time: string;
  date?: string;
  patient: string;
  clinician: string;
  consentStatus: "EMAIL_BOUNCED" | "NOT_SIGNED" | "SIGNED";
  emails: number;
  texts: number;
  reminders?: number;
  lastReminder?: string | null;
  daysUntilAppointment?: number;
} & Record<string, any>;

type UploadRow = {
  id: string;
  patient: string;
  appointmentDate: string;
  clinician: string;
  consentState: "SIGNED" | "PENDING";
  status: string;
  email: number;
  sms: number;
  signedDate: string | null;
  raw: Record<string, any>;
  isArchived?: boolean;
};
type TopViewKey = "transcription" | "waiting-room";

const getRequestedTopView = (search = "", hash = ""): TopViewKey => {
  const params = new URLSearchParams(search);
  const target = [
    params.get("view"),
    params.get("topView"),
    params.get("section"),
    params.get("focus"),
    hash,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (
    target.includes("transcription") ||
    target.includes("audio-recordings") ||
    target.includes("audio_recordings")
  ) {
    return "transcription";
  }

  return "waiting-room";
};

const shouldOpenTranscriptionReviewFromStorage = () => {
  if (typeof window === "undefined") return false;

  try {
    const raw = window.localStorage.getItem("dashboard_audio_review_focus");
    if (!raw) return false;

    const parsed = JSON.parse(raw);
    const createdAtMs = parsed?.createdAt ? new Date(parsed.createdAt).getTime() : 0;
    const isRecent =
      Number.isFinite(createdAtMs) && Date.now() - createdAtMs < 30 * 60 * 1000;

    return Boolean(parsed?.focus) && isRecent;
  } catch (_error) {
    return false;
  }
};

type AudioRecordingStatus =
  | "initiated"
  | "processing"
  | "in_progress"
  | "uploaded"
  | "generated"
  | "completed"
  | "failed"
  | "falied";

type RetryableRecordingStage = "transcript" | "patient-note";

type TranscriptionRow = {
  id: string;
  uploadSessionId: string | null;
  treatmentPlanId: number | null;
  consentBundleId: number | null;
  consentStatus: string | null;
  recordedAt: string;
  patientName: string;
  clinicianName: string;
  audioStatus: AudioRecordingStatus | string | null;
  transcriptStatus: AudioRecordingStatus | string | null;
  patientNoteStatus: AudioRecordingStatus | string | null;
  raw: Record<string, any>;
};

// ---------- Data (keep for other tabs only) ----------
const REMINDER_APPTS: Appointment[] = [];

const UPLOAD_ROWS: UploadRow[] = [
  {
    id: "u1",
    patient: "Michael Chen",
    appointmentDate: "2025-01-10",
    clinician: "Dr Brown",
    consentState: "SIGNED",
    status: "COMPLETED",
    email: 1,
    sms: 1,
    signedDate: "2025-12-21",
    raw: {},
  },
  {
    id: "u2",
    patient: "Robert Davis",
    appointmentDate: "2025-01-12",
    clinician: "Dr Brown",
    consentState: "SIGNED",
    status: "COMPLETED",
    email: 1,
    sms: 1,
    signedDate: "2025-12-17",
    raw: {},
  },
  {
    id: "u3",
    patient: "Robert Davis",
    appointmentDate: "2025-01-18",
    clinician: "Dr Brown",
    consentState: "PENDING",
    status: "SENT_UNSIGNED",
    email: 1,
    sms: 1,
    signedDate: null,
    raw: {},
  },
  {
    id: "u4",
    patient: "Michael Chen",
    appointmentDate: "2025-01-20",
    clinician: "Dr Brown",
    consentState: "PENDING",
    status: "SENT_UNSIGNED",
    email: 1,
    sms: 1,
    signedDate: null,
    raw: {},
  },
];

// ---------- Helpers ----------
const fmtLong = new Intl.DateTimeFormat("en-GB", {
  weekday: "long",
  month: "long",
  day: "numeric",
  year: "numeric",
}).format;

const fmtShort = (d: Date) =>
  new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d);

const todayLong = fmtLong(new Date());

// "2025-09-22 12:05" -> "12:05"
const hhmm = (dt?: string | null) => {
  if (!dt) return "00:00";
  const fixed = dt.includes("T") ? dt : dt.replace(" ", "T");
  const d = new Date(fixed);
  if (isNaN(d.getTime())) return "00:00";
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
};

const onlyDate = (dt?: string | null) => {
  if (!dt) return undefined;
  const t = dt.includes("T") ? dt.split("T")[0] : dt.split(" ")[0];
  return t || undefined;
};

const fmtSafeDate = (value?: string | null) => {
  if (!value || value === "-") return "-";

  // Keep already formatted UK dates like 02/06/2026 unchanged.
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value;

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;

  return fmtShort(d);
};

const fmtSafeDateTime = (value?: string | null) => {
  if (!value || value === "-") return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit", 
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const toAppointment = (raw: any, idx: number): Appointment => {
  const id = String(
    raw.id ?? raw.appointment_id ?? raw.uuid ?? `wr-${Date.now()}-${idx}`,
  );
  const time =
    raw.appointment_time ?? hhmm(raw.next_appointment_date) ?? "00:00";

  const patient =
    raw.patient?.first_name && raw.patient?.last_name
      ? `${raw.patient.first_name} ${raw.patient.last_name}`
      : (raw.patient_name ?? raw.patient ?? "Unknown Patient");

  const clinician = raw.clinician_name ?? raw.clinician ?? "Unknown";

  const isArchived = isArchivedRecord(raw);

  const rawStatus = String(
    raw?.status ??
      raw?.consent_status ??
      raw?.consentStatus ??
      raw?.consent_status_label ??
      "",
  ).trim();

  const status = isArchived ? "Archived" : rawStatus || "Draft";

  const consentRaw = String(
    raw.consent_status ?? raw.status ?? "",
  ).toUpperCase();
  let consentStatus: ConsentStatus = "NOT_SIGNED";
  if (consentRaw.includes("BOUNCE")) consentStatus = "EMAIL_BOUNCED";
  else if (consentRaw.includes("SIGNED")) consentStatus = "SIGNED";

  const emails = Number(raw.email_sent_count ?? raw.emails_count ?? 0);
  const texts = Number(raw.sms_sent_count ?? raw.sms_count ?? 0);

  const date = raw.next_appointment_date
    ? raw.next_appointment_date.split("T")[0] ||
      raw.next_appointment_date.split(" ")[0]
    : undefined;

  const reminders = Number(raw.no_of_reminders_sent ?? 0);

  const lastReminder = raw.last_reminder_sent_at
    ? raw.last_reminder_sent_at.split("T")[0] ||
      raw.last_reminder_sent_at.split(" ")[0]
    : null;

  const daysUntilAppointment =
    typeof raw.days_until_appointment === "number"
      ? raw.days_until_appointment
      : undefined;

  const dob =
    raw.patient?.date_of_birth ??
    raw.date_of_birth ??
    raw.patient_date_of_birth ??
    null;

  return {
    ...raw,
    raw,
    id,
    time,
    date,
    patient,
    clinician,
    status,
    isArchived,
    consentStatus,
    emails,
    texts,
    reminders,
    lastReminder,
    daysUntilAppointment,
    date_of_birth: dob,
    patient_email: raw.patient?.email ?? raw.patient_email ?? "",
  };
};

const toUploadRow = (raw: any, idx: number): UploadRow => {
  const id = String(
    raw.id ?? raw.treatment_plan_id ?? raw.uuid ?? `up-${Date.now()}-${idx}`,
  );
  const patient =
    raw?.patient?.first_name && raw?.patient?.last_name
      ? `${raw.patient.first_name} ${raw.patient.last_name}`
      : (raw.patient_name ?? raw.patient ?? "Unknown Patient");

  let appointmentDate: string | undefined;
  if (raw.treatment_appointment_dates) {
    try {
      const arr = JSON.parse(String(raw.treatment_appointment_dates));
      if (Array.isArray(arr) && arr.length > 0) {
        appointmentDate = onlyDate(arr[0]);
      }
    } catch {
      appointmentDate = onlyDate(String(raw.treatment_appointment_dates));
    }
  }
  if (!appointmentDate)
    appointmentDate = onlyDate(raw.next_appointment_date) ?? "1970-01-01";

  const clinician = raw.clinician_name ?? raw.clinician ?? "Unknown";
  const signedDate = raw.signed_date
    ? (onlyDate(raw.signed_date) ?? null)
    : null;
  const consentState: UploadRow["consentState"] = signedDate
    ? "SIGNED"
    : "PENDING";

  const isArchived = isArchivedRecord(raw);

  // status text (keep raw status but show Archived if archived)
  const baseStatus = String(
    raw.status ??
      (raw.uploaded_to_dentally_patient_record ? "Completed" : "Pending"),
  );
  const status = isArchived ? "Archived" : baseStatus;

  const email = Number(raw.email_sent_count ?? raw.emails_count ?? 0);
  const sms = Number(raw.sms_sent_count ?? raw.sms_count ?? 0);

  return {
    id,
    patient,
    appointmentDate,
    clinician,
    consentState,
    status,
    email,
    sms,
    signedDate,
    isArchived,
    raw: { ...raw },
  };
};

const toTranscriptionRow = (raw: any, idx: number): TranscriptionRow => {
  const treatmentPlanId = Number(raw?.treatment_plan_id);
  const consentBundleId = Number(raw?.consent_bundle_id);
  const recordedAt = raw?.recorded_at ?? "-";

  const patientName = String(
    raw?.patient_name ??
      raw?.patient?.full_name ??
      raw?.patient?.name ??
      raw?.patient ??
      "Unknown Patient",
  ).trim();

  const clinicianName = String(
    raw?.clinician_name ??
      raw?.clinician?.full_name ??
      raw?.clinician?.name ??
      raw?.clinician ??
      "Unknown Clinician",
  ).trim();

  return {
    id: [
      Number.isFinite(consentBundleId) ? consentBundleId : "no-consent",
      Number.isFinite(treatmentPlanId) ? treatmentPlanId : "no-treatment",
      recordedAt,
      idx,
    ].join("-"),
    uploadSessionId:
      typeof raw?.upload_session_id === "string" &&
      raw.upload_session_id.trim()
        ? raw.upload_session_id.trim()
        : null,
    treatmentPlanId: Number.isFinite(treatmentPlanId) ? treatmentPlanId : null,
    consentBundleId: Number.isFinite(consentBundleId) ? consentBundleId : null,
    consentStatus: raw?.consent_bundle_status ?? raw?.consent_bundle?.status ??
      raw?.consent_status ?? raw?.status ?? null,
    recordedAt,
    patientName: patientName || "-",
    clinicianName: clinicianName || "-",
    audioStatus: raw?.audio_status ?? null,
    transcriptStatus:
      raw?.transcript_status ?? raw?.transcription_status ?? null,
    patientNoteStatus:
      raw?.patient_note_status ??
      raw?.patient_note_generation_status ??
      raw?.patientNoteStatus ??
      null,
    raw,
  };
};

const normalizeRecordingFilter = (value: string) => value.trim().replace(/\s+/g, " ").toLowerCase();

const normalizeRecordingStatus = (status?: string | null) =>
  String(status ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const isPatientNoteReady = (status?: string | null) =>
  ["generated", "edited"].includes(normalizeRecordingStatus(status));

const isStageComplete = (status?: string | null) =>
  ["uploaded", "generated", "complete", "completed"].includes(
    normalizeRecordingStatus(status),
  );

const isStageFailed = (status?: string | null) =>
  ["failed", "falied", "error"].includes(normalizeRecordingStatus(status));

const isActiveProcessingStatus = (status?: string | null) =>
  [
    "initiated",
    "processing",
    "in_progress",
    "generating",
    "queued",
    "transcribing",
    "started",
    "running",
    "pending",
  ].includes(normalizeRecordingStatus(status));

const formatRecordingStatus = (
  status: string | null | undefined,
  completeLabel: string,
) => {
  const normalized = normalizeRecordingStatus(status);

  if (!normalized) return "Not started";
  if (isStageComplete(status)) return completeLabel;
  if (isStageFailed(status)) return "Failed";
  if (isActiveProcessingStatus(status)) return "Processing";

  return normalized
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getStageBadgeClass = (status?: string | null) => {
  if (isStageComplete(status)) return "transcriptionStageComplete";
  if (isStageFailed(status)) return "transcriptionStageFailed";
  if (isActiveProcessingStatus(status)) return "transcriptionProcessingActive";
  return "transcriptionStageWaiting";
};

const hasRecordingInProgress = (row: TranscriptionRow) =>
  isActiveProcessingStatus(row.audioStatus) ||
  isActiveProcessingStatus(row.transcriptStatus) ||
  isActiveProcessingStatus(row.patientNoteStatus);

const hasRecordingFailed = (row: TranscriptionRow) =>
  isStageFailed(row.audioStatus) ||
  isStageFailed(row.transcriptStatus) ||
  isStageFailed(row.patientNoteStatus);

const TRANSCRIPTION_POLL_INTERVAL_MS = 30_000;
const CONSENT_BUNDLE_CREATED_EVENT = "medicube:consent-bundle-created";
const DRAFT_AUDIO_RECORD_STORAGE_KEY = "activeDraftAudioRecordData";

const getDraftTreatmentPlanId = (bundle: Record<string, any>) => {
  const value =
    bundle?.treatment_plan_id ??
    bundle?.treatmentPlanId ??
    bundle?.treatment_plan?.treatment_plan_id ??
    bundle?.treatment_plan?.id ??
    bundle?.treatment?.treatment_plan_id ??
    bundle?.treatment?.id;

  return value == null ? "" : String(value).trim();
};

const unwrapAudioRecordData = (response: any) =>
  response?.data?.audio_record ??
  response?.data?.audio_recording ??
  response?.result?.audio_record ??
  response?.result?.audio_recording ??
  response?.audio_record ??
  response?.audio_recording ??
  response?.data ??
  response?.result ??
  response;

const isDraftConsentBundle = (bundle: Record<string, any> | null | undefined) =>
  !isArchivedRecord(bundle) &&
  String(bundle?.status ?? bundle?.consent_status ?? "")
    .trim().toLowerCase() === "draft";

const filterDraftTranscriptions = async (rows: TranscriptionRow[]) => {
  // Older recording responses omit bundle status. Check the bundle instead of
  // assuming a generated patient note still belongs to an editable draft.
  const bundleChecks = new Map<number, Promise<boolean>>();
  const checkedRows = await Promise.all(rows.map(async (row) => {
    if (isArchivedRecord(row.raw) || isArchivedRecord(row.raw.consent_bundle)) return null;

    const status = row.consentStatus;
    if (typeof status === "string" && status.trim()) {
      return isDraftConsentBundle({ status }) ? row : null;
    }

    const bundleId = row.consentBundleId;
    if (!bundleId) return null;

    if (!bundleChecks.has(bundleId)) {
      bundleChecks.set(bundleId, axios.get(`/consent_bundle/${bundleId}`, {
        headers: { Accept: "application/json" },
      }).then(({ data }) => isDraftConsentBundle(data?.consent_bundle ?? data)));
    }

    return await bundleChecks.get(bundleId) ? { ...row, consentStatus: "Draft" } : null;
  }));

  return checkedRows.filter((row): row is TranscriptionRow => row !== null);
};


// ---------- Component ----------
const Dashboard: React.FC = () => {
  const [savedFilters] = useState(readDashboardFilters);
  const [clinician, setClinician] = useState(savedFilters.clinician);
  const [consent, setConsent] = useState(savedFilters.consent);
  const [query, setQuery] = useState(savedFilters.query);
  const [activeTab, setActiveTab] = useState<TabKey>(savedFilters.activeTab);
  const [dashboardRefreshVersion, setDashboardRefreshVersion] = useState(0);
  const [rowSort, setRowSort] = useState<DashboardSort>(null);
  const [todaySelected, setTodaySelected] = useState<Set<string>>(new Set());
  const changeSort = (field: NonNullable<DashboardSort>["field"]) => {
    setRowSort((current) => ({ field, direction: current?.field === field && current.direction === "asc" ? "desc" : "asc" }));
    setPage(1);
    setPageAll(1);
    setPageUpload(1);
  };
  const sortHeader = (field: NonNullable<DashboardSort>["field"], label: string) => {
    const active = rowSort?.field === field;
    const ascending = active && rowSort?.direction === "asc";
    const nextOrder = field === "treatmentValue"
      ? ascending ? "highest first" : "lowest first"
      : ascending ? "latest first" : "earliest first, unbooked first";
    return (
      <div role="columnheader" aria-sort={active ? ascending ? "ascending" : "descending" : "none"}>
        <button type="button" className={styles.sortHeader} onClick={() => changeSort(field)} aria-label={`Sort ${label.toLowerCase()}: ${nextOrder}`}>
          {label}
          <span className={styles.sortArrows} aria-hidden="true">
            <FiArrowUp className={active && ascending ? styles.activeSortArrow : ""} />
            <FiArrowDown className={active && !ascending ? styles.activeSortArrow : ""} />
          </span>
        </button>
      </div>
    );
  };
  const [showDashboardTutorial, setShowDashboardTutorial] = useState(false);
  // QR modal state
  const [qrOpen, setQrOpen] = useState(false);
  const [selectedAppt, setSelectedAppt] = useState<any>(null);
  // Archive confirmation state
  const [confirmArchive, setConfirmArchive] = useState<{
    open: boolean;
    id?: string | number;
    name?: string;
    source: "all" | "upload" | "reminders" | null;
    loading: boolean;
  }>({ open: false, source: null, loading: false });

  const [reminderSelected, setReminderSelected] = useState<Set<string>>(new Set(),);
  const [uploadSelected, setUploadSelected] = useState<Set<string>>(new Set());
  const [isBulkArchiving, setIsBulkArchiving] = useState(false);
  const [isSendingReminders, setIsSendingReminders] = useState(false);
  // Today
  const [todayAppts, setTodayAppts] = useState<Appointment[]>([]);
  const [loadingToday, setLoadingToday] = useState<boolean>(false);
  const [errorToday, setErrorToday] = useState<string | null>(null);
  // Reminders
  const [reminderAppts, setReminderAppts] = useState<Appointment[]>([]);
  const [loadingRem, setLoadingRem] = useState<boolean>(false);
  const [errorRem, setErrorRem] = useState<string | null>(null);
  const [totalRem, setTotalRem] = useState<number>(0);
  // Upload
  const [uploadRows, setUploadRows] = useState<UploadRow[]>([]);
  const [loadingUpload, setLoadingUpload] = useState<boolean>(false);
  const [errorUpload, setErrorUpload] = useState<string | null>(null);
  const [totalUpload, setTotalUpload] = useState<number>(0);
  // shared pagination
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(25);
  const [total, setTotal] = useState<number>(0);
  // upload tab pagination
  const [pageUpload, setPageUpload] = useState<number>(1);
  const [perPageUpload, setPerPageUpload] = useState<number>(25);
  // user + clinicians
  const [user, setUser] = React.useState<any>(null);
  const [clinicians, setClinicians] = React.useState<Array<{ value: string; label: string }>>([]);

  const navigate = useNavigate();
  const location = useLocation();
  const isPracticeVersion = Boolean(user?.medicube_practice_id);

  const dashboardTutorialSteps = useMemo<DashboardTutorialStep[]>(
    () => [
      {
        id: "dashboard-overview",
        selector: ".tutorial-dashboard-table",
        title: "Dashboard overview",
        text: "Review patients, treatment values, clinicians, consent status, next appointments, email activity, and available actions. Hover over an email count for SMS and signing activity.",
        placement: "top",
      },
      {
        id: "dashboard-filters",
        selector: ".tutorial-dashboard-filters",
        title: "Use filters",
        text: "Use this filter area to narrow records by clinician, status, or patient. The toggle controls whether filters are cleared when you change tabs.",
        placement: "bottom",
      },
      {
        id: "clinician-filter",
        selector: ".tutorial-clinician-filter",
        title: "Clinician filter",
        text: "Filter the dashboard by clinician when you only want to review bundles assigned to one clinician.",
        placement: "bottom",
      },
      {
        id: "status-filter",
        selector: ".tutorial-status-filter",
        title: "Status filter",
        text: "Use the status filter to find drafts, bundles sent for signing, or completed consents.",
        placement: "bottom",
      },
      {
        id: "patient-search",
        selector: ".tutorial-patient-search",
        title: "Search by patient",
        text: "Search for a patient name to find their consent bundles quickly without scrolling through the full table.",
        placement: "bottom",
      },
      {
        id: "dashboard-tabs",
        selector: ".tutorial-tabs",
        title: "Dashboard tabs",
        text: "Use these tabs to switch between all records, appointments due today, records that need reminders, and bundles ready for upload.",
        placement: "bottom",
      },
      {
        id: "row-status",
        selector: ".tutorial-row-status",
        title: "Consent status",
        text: "Consent status shows Draft, Sent for signing, or Completed, including digital signatures and uploaded paper consents.",
        placement: "top",
      },
      {
        id: "next-appointment",
        selector: ".tutorial-next-appointment",
        title: "Next appointment",
        text: "The next recorded appointment appears in grey. Unbooked appointments and their treatment values appear in soft red. Sort appointments earliest or latest, or treatment values lowest or highest. Hover over a treatment value for the full amount.",
        placement: "top",
      },
      {
        id: "communication-counts",
        selector: ".tutorial-communication-counts",
        title: "Email and SMS activity",
        text: "The email counter shows sent emails. Hover over it to see SMS counts and signing or reminder activity where available.",
        placement: "top",
      },
      {
        id: "dashboard-actions",
        selector: ".tutorial-dashboard-actions",
        title: "Row actions",
        text: "Use these buttons to quick view the bundle, send reminders, download reports, or archive records without leaving the dashboard.",
        placement: "left",
      },
      {
        id: "quick-view",
        selector: ".tutorial-quick-view",
        title: "Quick view",
        text: "Click the eye icon to review the patient interaction summary for a consent bundle.",
        placement: "left",
      },
      {
        id: "video-watch-summary",
        selector: ".tutorial-video-watch-summary",
        title: "Interaction summary",
        text: "When quick view is open, this section shows how the patient interacted with the bundle, including consent activity and video watch information where available.",
        placement: "top",
        optional: true,
      },
    ],
    [],
  );
  const consentParam = (c: string | undefined) => {
    if (c === "signed") return "SIGNED";
    if (c === "not_signed") return "NOT_SIGNED";
    if (c === "email_bounced") return "EMAIL_BOUNCED";
    return undefined;
  };

  // All tab
  const [allRows, setAllRows] = useState<any[]>([]);
  const [loadingAll, setLoadingAll] = useState<boolean>(false);
  const [errorAll, setErrorAll] = useState<string | null>(null);
  const [totalAll, setTotalAll] = useState<number>(0);
  const [pageAll, setPageAll] = useState<number>(1);
  const [perPageAll, setPerPageAll] = useState<number>(25);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingReminderIds, setPendingReminderIds] = useState<number[]>([]);
  const [statusFilter, setStatusFilter] = useState(savedFilters.statusFilter);
  const [consentStatusParam, setConsentStatusParam] = useState<string | undefined>(
    savedFilters.statusFilter === "all" ? undefined : savedFilters.statusFilter,
  );
  const [autoClearFilters, setAutoClearFilters] = useState(savedFilters.autoClearFilters);

  const [patientQuery, setPatientQuery] = useState(savedFilters.patientQuery);
  const [patientOptions, setPatientOptions] = useState<DashboardPatientOption[]>([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [patientSearchError, setPatientSearchError] = useState("");
  const [patientSearchAttempt, setPatientSearchAttempt] = useState(0);
  const [selectedPatientId, setSelectedPatientId] = useState<number | null>(
    savedFilters.selectedPatientId,
  );
  const [selectedAllTab, setSelectedAllTab] = useState<Set<string>>(new Set());
  const [downloadingIds, setDownloadingIds] = useState(new Set());
  const [bulkDownloading, setBulkDownloading] = useState(false);

  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [quickViewLoading, setQuickViewLoading] = useState(false);
  const [quickViewError, setQuickViewError] = useState("");
  const [interactionLog, setInteractionLog] = useState<InteractionLog>({});
  const [quickViewId, setQuickViewId] = useState(null);
  const [openingConsentBundleId, setOpeningConsentBundleId] = useState<
    string | null
  >(null);

  // const [topView, setTopView] = useState<TopViewKey>("waiting-room");
  // const transcriptionReviewCount = 2;

  const [topView, setTopView] = useState<TopViewKey>(() => {
    if (shouldOpenTranscriptionReviewFromStorage()) return "transcription";

    if (typeof window === "undefined") return "waiting-room";

    return getRequestedTopView(window.location.search, window.location.hash);
  });
  const [transcriptionRows, setTranscriptionRows] = useState<TranscriptionRow[]>([]);
  const [transcriptionPatientQuery, setTranscriptionPatientQuery] = useState(savedFilters.transcriptionPatientQuery);
  const [transcriptionClinician, setTranscriptionClinician] = useState(savedFilters.transcriptionClinician);
  const [loadingTranscriptions, setLoadingTranscriptions] = useState(false);
  const [pollingTranscriptions, setPollingTranscriptions] = useState(false);
  const [lastTranscriptionPollAt, setLastTranscriptionPollAt] = useState<string | null>(null);
  const [errorTranscriptions, setErrorTranscriptions] = useState<string | null>(null,);
  const [reviewingTranscriptionId, setReviewingTranscriptionId] = useState<string | null>(null);
  const [retryingRecordingStage, setRetryingRecordingStage] = useState<string | null>(null);

  useEffect(() => {
    const refreshDashboard = () => {
      setDashboardRefreshVersion((currentVersion) => currentVersion + 1);
    };

    window.addEventListener(CONSENT_BUNDLE_CREATED_EVENT, refreshDashboard);
    window.addEventListener("focus", refreshDashboard);
    window.addEventListener("medicube:appointment-updated", refreshDashboard);

    return () => {
      window.removeEventListener(CONSENT_BUNDLE_CREATED_EVENT, refreshDashboard);
      window.removeEventListener("focus", refreshDashboard);
      window.removeEventListener("medicube:appointment-updated", refreshDashboard);
    };
  }, []);

  useEffect(() => {
    const requestedTopView = getRequestedTopView(location.search, location.hash);
    const openFromStorage = shouldOpenTranscriptionReviewFromStorage();

    if (requestedTopView === "transcription" || openFromStorage) {
      setTopView("transcription");
      window.localStorage.removeItem("dashboard_audio_review_focus");
    }
  }, [location.search, location.hash]);

  const transcriptionPatientOptions = useMemo(() => Array.from(new Map<string, string>(transcriptionRows.map((row) =>
    [normalizeRecordingFilter(row.patientName), row.patientName])).values()).sort((a, b) => a.localeCompare(b)), [transcriptionRows]);
  const transcriptionClinicianOptions = useMemo(() => Array.from(new Map<string, string>(transcriptionRows.map((row) =>
    [normalizeRecordingFilter(row.clinicianName), row.clinicianName])).entries())
    .map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label)), [transcriptionRows]);
  const filteredTranscriptionRows = useMemo(() => {
    const patient = normalizeRecordingFilter(transcriptionPatientQuery);
    return transcriptionRows.filter((row) => (!patient || normalizeRecordingFilter(row.patientName).includes(patient)) &&
      (transcriptionClinician === "all" || normalizeRecordingFilter(row.clinicianName) === transcriptionClinician));
  }, [transcriptionRows, transcriptionPatientQuery, transcriptionClinician]);
  const hasTranscriptionFilters = Boolean(transcriptionPatientQuery.trim()) || transcriptionClinician !== "all";

  const transcriptionProcessingCount = useMemo(
    () => transcriptionRows.filter(hasRecordingInProgress).length,
    [transcriptionRows],
  );

  const transcriptionFailedCount = useMemo(
    () => transcriptionRows.filter(hasRecordingFailed).length,
    [transcriptionRows],
  );

const transcriptionPollLabel = pollingTranscriptions
    ? "Checking updates..."
    : transcriptionProcessingCount > 0
      ? `${transcriptionProcessingCount} processing`
      : transcriptionFailedCount > 0
        ? `${transcriptionFailedCount} failed`
        : "Up to date";

const transcriptionPollMeta = lastTranscriptionPollAt
    ? `Updated ${lastTranscriptionPollAt}`
    : "Polls every 30s";

  useEffect(() => {
    if (topView !== "transcription") return;

    let cancelled = false;
    let requestInFlight = false;

    const fetchTranscriptions = async (silent = false) => {
      if (requestInFlight) return;

      requestInFlight = true;

      if (silent) {
        setPollingTranscriptions(true);
      } else {
        setLoadingTranscriptions(true);
        setErrorTranscriptions(null);
      }

      try {
        const base = process.env.REACT_APP_BASE_URL_V2;
        if (!base) {
          throw new Error("REACT_APP_BASE_URL_V2 is not set");
        }

        const endpoint = `${base.replace(/\/+$/, "")}/audio-recording/get_all_audio_recordings`;

        const resp = await axios.get(endpoint, {
          headers: {
            Accept: "application/json",
          },
        });

        const payload = Array.isArray(resp.data?.data)
          ? resp.data.data
          : Array.isArray(resp.data?.audio_recordings)
            ? resp.data.audio_recordings
            : Array.isArray(resp.data)
              ? resp.data
              : resp.data?.consent_bundle_id != null
                ? [resp.data]
                : [];

        const normalized: TranscriptionRow[] = payload.map((recording: any, index: number) =>
          toTranscriptionRow(recording, index),
        );

        const deduplicated = Array.from(
          new Map(
            normalized.map((recording) => [
              [
                recording.consentBundleId ?? "no-consent",
                recording.treatmentPlanId ?? "no-treatment",
                recording.recordedAt,
              ].join("-"),
              recording,
            ]),
          ).values(),
        );

        const draftRows = await filterDraftTranscriptions(deduplicated);

        if (cancelled) return;

        setTranscriptionRows(draftRows);
        setLastTranscriptionPollAt(
          new Intl.DateTimeFormat("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          }).format(new Date()),
        );
        setErrorTranscriptions(null);
      } catch (e: any) {
        console.error("Failed to load audio recordings", e);

        if (cancelled) return;

        if (!silent) {
          setTranscriptionRows([]);
        }

        setErrorTranscriptions(
          e?.response?.data?.error ??
            e?.response?.data?.detail ??
            e?.message ??
            "Failed to load audio recordings",
        );
      } finally {
        if (!cancelled) {
          setLoadingTranscriptions(false);
          setPollingTranscriptions(false);
        }

        requestInFlight = false;
      }
    };

    fetchTranscriptions(false);

    const pollTimer = window.setInterval(() => {
      fetchTranscriptions(true);
    }, TRANSCRIPTION_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(pollTimer);
    };
  }, [topView, dashboardRefreshVersion]);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoadingToday(true);
      setErrorToday(null);

      try {
        const params: Record<string, any> = {
          page,
          per_page: perPage,
        };

        if (clinician !== "all") params.clinician = clinician;

        const cs = consentParam(consent);
        if (cs) params.consent_status = cs;

        if (selectedPatientId) params.patient_id = selectedPatientId;
        if (consentStatusParam) params.consent_status = consentStatusParam;

        const endpoint = `${process.env.REACT_APP_BASE_URL_V2}dashboard/waiting-room`;

        const resp = await loadDashboardPage((requestParams) => axios.get(endpoint, {
          params: requestParams,
          headers: { Accept: "application/json" },
        }), params, rowSort);

        if (cancelled) return;

        const payload = Array.isArray(resp.data?.data)
          ? resp.data.data
          : Array.isArray(resp.data)
            ? resp.data
            : [];

        const normalized = (payload as any[]).map((r, i) =>
          toAppointment(r, i),
        );
        const dedup = Array.from(
          new Map(normalized.map((a) => [a.id, a])).values(),
        );

        setTodayAppts(dedup);
        const nextTotal = Number(resp.data?.meta?.total ?? dedup.length);
        setTotal(nextTotal);
        const lastPage = Math.max(1, Math.ceil(nextTotal / perPage));
        if (page > lastPage) setPage(lastPage);
      } catch (e: any) {
        if (cancelled) return;
        console.error(e);
        setErrorToday(
          e?.response?.data?.detail ??
            e?.message ??
            "Failed to load waiting room",
        );
      } finally {
        if (!cancelled) setLoadingToday(false);
      }
    };

    if (activeTab === "today") fetchData();

    return () => {
      cancelled = true;
    };
  }, [
    activeTab,
    page,
    perPage,
    clinician,
    consent,
    selectedPatientId,
    consentStatusParam,
    dashboardRefreshVersion,
    rowSort,
  ]);

  // Future appointments call
  useEffect(() => {
    let cancelled = false;

    const fetchReminders = async () => {
      setLoadingRem(true);
      setErrorRem(null);
      try {
        const params: Record<string, any> = {
          page,
          per_page: perPage,
        };
        if (clinician !== "all") params.clinician = clinician;

        const base = process.env.REACT_APP_BASE_URL_V2;

        if (selectedPatientId) params.patient = selectedPatientId;
        if (consentStatusParam) params.consent_status = consentStatusParam;
        const endpoint = `${process.env.REACT_APP_BASE_URL_V2}dashboard/future-appointments`;

        const resp = await loadDashboardPage((requestParams) => axios.get(endpoint, {
          params: requestParams,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }), params, rowSort);

        if (cancelled) return;

        const payload = Array.isArray(resp.data?.data)
          ? resp.data.data
          : Array.isArray(resp.data)
            ? resp.data
            : [];

        const normalized = (payload as any[]).map((r, i) =>
          toAppointment(r, i),
        );
        const dedup = Array.from(
          new Map(normalized.map((a) => [a.id, a])).values(),
        );
        setReminderAppts(dedup);
        const nextTotal = Number(resp.data?.meta?.total ?? dedup.length);
        setTotalRem(nextTotal);
        const lastPage = Math.max(1, Math.ceil(nextTotal / perPage));
        if (page > lastPage) setPage(lastPage);
      } catch (e: any) {
        if (cancelled) return;
        console.error(e);
        setErrorRem(
          e?.response?.data?.detail ??
            e?.message ??
            "Failed to load future appointments",
        );
      } finally {
        if (!cancelled) setLoadingRem(false);
      }
    };

    if (activeTab === "reminders") {
      fetchReminders();
    }

    return () => {
      cancelled = true;
    };
  }, [
    activeTab,
    page,
    perPage,
    clinician,
    selectedPatientId,
    consentStatusParam,
    dashboardRefreshVersion,
    rowSort,
  ]);

  // Ready for upload call
  useEffect(() => {
    let cancelled = false;

    const fetchUpload = async () => {
      setLoadingUpload(true);
      setErrorUpload(null);
      try {
        const params: Record<string, any> = {
          page: pageUpload,
          per_page: perPageUpload,
        };
        if (clinician !== "all") params.clinician = clinician;

        const base = process.env.REACT_APP_BASE_URL_V2;
        if (!base) throw new Error("REACT_APP_BASE_URL_V2 is not set");

        if (selectedPatientId) params.patient = selectedPatientId;
        if (consentStatusParam) params.consent_status = consentStatusParam;
        const endpoint = `${process.env.REACT_APP_BASE_URL_V2}dashboard/ready-for-upload`;

        const resp = await loadDashboardPage((requestParams) => axios.get(endpoint, {
          params: requestParams,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }), params, rowSort);

        if (cancelled) return;

        const payload = Array.isArray(resp.data?.data)
          ? resp.data.data
          : Array.isArray(resp.data)
            ? resp.data
            : [];

        const normalized = (payload as any[]).map((r, i) => toUploadRow(r, i));
        const dedup = Array.from(
          new Map(normalized.map((a) => [a.id, a])).values(),
        );

        setUploadRows(dedup);
        const nextTotal = Number(resp.data?.meta?.total ?? dedup.length);
        setTotalUpload(nextTotal);
        const lastPage = Math.max(1, Math.ceil(nextTotal / perPageUpload));
        if (pageUpload > lastPage) setPageUpload(lastPage);
      } catch (e: any) {
        if (cancelled) return;
        console.error(e);
        setErrorUpload(
          e?.response?.data?.detail ??
            e?.message ??
            "Failed to load ready-for-upload",
        );
      } finally {
        if (!cancelled) setLoadingUpload(false);
      }
    };

    if (activeTab === "upload") fetchUpload();

    return () => {
      cancelled = true;
    };
  }, [
    activeTab,
    pageUpload,
    perPageUpload,
    clinician,
    selectedPatientId,
    consentStatusParam,
    dashboardRefreshVersion,
    rowSort,
  ]);

  // all
  useEffect(() => {
    let cancelled = false;

    const fetchAll = async () => {
      setLoadingAll(true);
      setErrorAll(null);

      try {
        const base = process.env.REACT_APP_BASE_URL_V2;

        const params: Record<string, any> = {
          page: pageAll,
          per_page: perPageAll,
        };

        if (clinician !== "all") params.clinician = clinician;
        if (selectedPatientId) params.patient = selectedPatientId;
        if (consentStatusParam) params.consent_status = consentStatusParam;

        const endpoint = `${base}dashboard/all`;

        const resp = await loadDashboardPage((requestParams) => axios.get(endpoint, {
          params: requestParams,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }), params, rowSort);

        if (cancelled) return;

        const payload = Array.isArray(resp.data?.data)
          ? resp.data.data
          : Array.isArray(resp.data)
            ? resp.data
            : [];

        const normalized = (payload as any[]).map((r, i) =>
          toAppointment(r, i),
        );
        const dedup = Array.from(
          new Map(normalized.map((a) => [a.id, a])).values(),
        );

        setAllRows(dedup);
        const nextTotal = Number(resp.data?.meta?.total ?? dedup.length);
        setTotalAll(nextTotal);
        const lastPage = Math.max(1, Math.ceil(nextTotal / perPageAll));
        if (pageAll > lastPage) setPageAll(lastPage);
      } catch (e: any) {
        if (cancelled) return;
        console.error(e);
        setErrorAll(
          e?.response?.data?.detail ??
            e?.message ??
            "Failed to load all dashboard data",
        );
      } finally {
        if (!cancelled) setLoadingAll(false);
      }
    };

    if (activeTab === "all") fetchAll();

    return () => {
      cancelled = true;
    };
  }, [
    activeTab,
    pageAll,
    perPageAll,
    clinician,
    selectedPatientId,
    consentStatusParam,
    statusFilter,
    dashboardRefreshVersion,
    rowSort,
  ]);

  // get user + clinicians
  useEffect(() => {
    const getUserData = async () => {
      try {
        const { data } = await axios.get("/user", {
          headers: { Accept: "application/json" },
        });

        const u = Array.isArray(data)
          ? typeof data[1] === "object" && data[1] !== null
            ? data[1]
            : data[0]
          : data;

        setUser(u);

        if (u?.medicube_practice_id) {
          await getClinicians(u.medicube_practice_id);
        } else {
          console.warn("No medicube_practice_id on user payload:", u);
        }

        return u;
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    const getClinicians = async (practiceId: number) => {
      try {
        const { data } = await axios.get("/users/dropdown/clinicians", {
          params: { practice: practiceId },
          headers: { Accept: "application/json" },
        });

        const arr = Array.isArray(data) ? data : [];
        setClinicians(
          arr.map((c: any) => ({
            value: String(c.value),
            label: String(c.label),
          })),
        );
        return arr;
      } catch (error) {
        console.error("Error fetching clinicians:", error);
      }
    };

    getUserData();
  }, []);

  // ---- Filters for all ----
  const filteredAll = useMemo(() => {
    return allRows.filter((a) => {
      const s = getConsentDisplayStatus(a.raw ?? a).toLowerCase();
      const stOk =
        statusFilter === "all" || s.includes(statusFilter.toLowerCase());
      const qOk =
        !query ||
        a.patient.toLowerCase().includes(query.toLowerCase()) ||
        a.clinician.toLowerCase().includes(query.toLowerCase());
      return qOk && stOk;
    });
  }, [allRows, query, statusFilter]);

  // ---- Filters for Today ----
  const todayFiltered = useMemo(() => {
    return todayAppts.filter((a) => {
      // --- Consent filter (correct & consistent) ---
      const csOk =
        consent === "all" ||
        (consent === "signed" && a.consentStatus === "SIGNED") ||
        (consent === "not_signed" && a.consentStatus === "NOT_SIGNED") ||
        (consent === "email_bounced" && a.consentStatus === "EMAIL_BOUNCED");

      // --- Search filter ---
      const qOk =
        !query ||
        a.patient.toLowerCase().includes(query.toLowerCase()) ||
        a.clinician.toLowerCase().includes(query.toLowerCase());

      // --- Status filter ---
      const s = getConsentDisplayStatus(a.raw ?? a).toLowerCase();

      const stOk =
        statusFilter === "all" || s.includes(statusFilter.toLowerCase());

      return csOk && qOk && stOk;
    });
  }, [consent, query, todayAppts, statusFilter]);

  // ---- Filters for Reminders ----
  const remindersFiltered = useMemo(() => {
    const rows = reminderAppts;

    return rows.filter((a) => {
      const statusLower = String(a.status || "")
        .toLowerCase()
        .trim();
      if (statusLower === "draft") return false;

      const qOk =
        !query ||
        a.patient.toLowerCase().includes(query.toLowerCase()) ||
        a.clinician.toLowerCase().includes(query.toLowerCase());

      const csOk =
        consent === "all" ||
        (consent === "signed" && a.consentStatus === "SIGNED") ||
        (consent === "not_signed" && a.consentStatus === "NOT_SIGNED") ||
        (consent === "email_bounced" && a.consentStatus === "EMAIL_BOUNCED");

      const s = getConsentDisplayStatus(a.raw ?? a).toLowerCase();
      const stOk =
        statusFilter === "all" || s.includes(statusFilter.toLowerCase());

      return qOk && csOk && stOk;
    });
  }, [reminderAppts, query, consent, statusFilter]);

  // ---- Filters for Upload ----
  const uploadFiltered = useMemo(() => {
    // The endpoint filters clinician IDs; rows contain the clinician's display name.
    const rows = uploadRows;

    return rows.filter((r) => {
      const qOk =
        !query ||
        r.patient.toLowerCase().includes(query.toLowerCase()) ||
        r.clinician.toLowerCase().includes(query.toLowerCase());

      const st = String(statusFilter || "all");
      const s = getConsentDisplayStatus(r.raw).toLowerCase();
      const isArchived = isArchivedRecord(r);

      const stOk =
        st === "all"
          ? true
          : st === "Archived"
            ? isArchived
            : !isArchived && s.includes(st.toLowerCase());

      return qOk && stOk;
    });
  }, [uploadRows, query, statusFilter]);

  const quickViewRows = topView !== "waiting-room"
    ? []
    : activeTab === "all" && !loadingAll && !errorAll
      ? filteredAll
      : activeTab === "reminders" && !loadingRem && !errorRem
        ? remindersFiltered
        : activeTab === "upload" && !loadingUpload && !errorUpload
          ? uploadFiltered
          : [];
  const { histories, rememberHistory } = useInteractionHistory(
    quickViewRows.map((row) => row.raw?.id ?? row.id),
    `${activeTab}:${dashboardRefreshVersion}`,
  );

  const selectedUploadArchiveIds = uploadFiltered
    .filter((row) => uploadSelected.has(row.id) && !isArchivedRecord(row))
    .map((row) => Number(row.raw?.id ?? row.id))
    .filter((id) => Number.isSafeInteger(id) && id > 0);

  const bulkRows: (Appointment | UploadRow)[] = activeTab === "all" ? (!loadingAll && !errorAll ? filteredAll : [])
    : activeTab === "today" ? (!loadingToday && !errorToday ? todayFiltered : [])
      : activeTab === "reminders" ? (!loadingRem && !errorRem ? remindersFiltered : [])
        : !loadingUpload && !errorUpload ? uploadFiltered : [];
  const activeSelection = activeTab === "all" ? selectedAllTab
    : activeTab === "today" ? todaySelected
      : activeTab === "reminders" ? reminderSelected : uploadSelected;
  const setActiveSelection = activeTab === "all" ? setSelectedAllTab
    : activeTab === "today" ? setTodaySelected
      : activeTab === "reminders" ? setReminderSelected : setUploadSelected;
  const selectedBulkRows = bulkRows.filter((row) => activeSelection.has(row.id));
  const selectedBundleIds = Array.from(new Set(selectedBulkRows
    .map((row) => Number(row.raw?.id ?? row.id))
    .filter((id) => Number.isSafeInteger(id) && id > 0)));
  const selectionIncludesDraft = selectedBulkRows.some((row) =>
    getConsentDisplayStatus(row.raw ?? row) === "Draft");
  const bulkActionsBusy = bulkDownloading || isSendingReminders || isBulkArchiving;

  useEffect(() => {
    saveDashboardFilters({
      activeTab,
      autoClearFilters,
      clinician,
      consent,
      statusFilter,
      query,
      patientQuery,
      selectedPatientId,
      transcriptionPatientQuery,
      transcriptionClinician,
    });
  }, [activeTab, autoClearFilters, clinician, consent, statusFilter, query, patientQuery, selectedPatientId, transcriptionPatientQuery, transcriptionClinician]);

  const hasActiveFilters =
    clinician !== "all" ||
    consent !== "all" ||
    statusFilter !== "all" ||
    Boolean(query || patientQuery || selectedPatientId || consentStatusParam);

  const resetFilterPages = () => {
    setPage(1);
    setPageUpload(1);
    setPageAll(1);
  };
  const clearClinicianFilter = () => {
    setClinician("all");
    resetFilterPages();
  };
  const clearStatusFilter = () => {
    setStatusFilter("all");
    setConsent("all");
    setConsentStatusParam(undefined);
    resetFilterPages();
  };

  const clearDashboardFilters = () => {
    setPatientQuery("");
    setSelectedPatientId(null);
    setQuery("");
    setConsent("all");
    setConsentStatusParam(undefined);
    setStatusFilter("all");
    setClinician("all");
    setPatientOptions([]);
    setPatientSearchError("");
    resetFilterPages();
  };

  const clearPatientSearch = () => {
    setSelectedPatientId(null);
    setPatientQuery("");
    setPatientOptions([]);
    setPatientSearchError("");
    resetFilterPages();
  };

  const changeDashboardTab = (nextTab: TabKey) => {
    if (nextTab === activeTab) return;
    if (autoClearFilters) clearDashboardFilters();
    setActiveTab(nextTab);
  };

  // ---- Select helpers ----
  const toggleReminderRow = (id: string) =>
    setReminderSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const toggleReminderAll = () => {
    const allIds = remindersFiltered.map((r) => r.id);
    const allSelected = allIds.every((id) => reminderSelected.has(id));
    setReminderSelected(allSelected ? new Set() : new Set(allIds));
  };

  const toggleUploadRow = (id: string) =>
    setUploadSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const toggleUploadAll = () => {
    const allIds = uploadFiltered.map((r) => r.id);
    const allSelected = allIds.every((id) => uploadSelected.has(id));
    setUploadSelected(allSelected ? new Set() : new Set(allIds));
  };

  const openQr = (a: Appointment) => {
    setSelectedAppt(a);
    setQrOpen(true);
  };

  // ---- Archive helpers ----
  const canArchiveRecord = (id: string | number, source: typeof confirmArchive.source) => {
    const rows = source === "all" ? allRows
      : source === "upload" ? uploadRows
      : source === "reminders" ? reminderAppts : [];
    const row = rows.find((item) => String(item.raw?.id ?? item.id) === String(id));
    return Boolean(row) && !isArchivedRecord(row);
  };

  const askArchive = (
    id: string | number,
    name: string,
    source: "all" | "upload" | "reminders",
  ) => {
    if (!canArchiveRecord(id, source)) return;
    setConfirmArchive({ open: true, id, name, source, loading: false });
  };

  const doArchive = async () => {
    if (!confirmArchive.id || confirmArchive.loading) return;
    if (!canArchiveRecord(confirmArchive.id, confirmArchive.source)) {
      setConfirmArchive({ open: false, source: null, loading: false });
      showNotify("error", "This record is already archived or no longer available.");
      return;
    }

    try {
      setConfirmArchive((p) => ({ ...p, loading: true }));

      await axios.delete(
        `consent_bundle/${confirmArchive.id}/archive_consent_bundle`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );

      // Remove the archived row immediately, then reload rows and totals.
      if (confirmArchive.source === "all") {
        setAllRows((prev) =>
          prev.filter(
            (r) => String(r.raw?.id ?? r.id) !== String(confirmArchive.id),
          ),
        );
      } else if (confirmArchive.source === "upload") {
        setUploadRows((prev) =>
          prev.filter(
            (r) => String(r.raw?.id ?? r.id) !== String(confirmArchive.id),
          ),
        );
      } else if (confirmArchive.source === "reminders") {
        setReminderAppts((prev) =>
          prev.filter(
            (r) => String(r.raw?.id ?? r.id) !== String(confirmArchive.id),
          ),
        );
      }

      const archivedId = String(confirmArchive.id);
      setSelectedAllTab((selected) =>
        new Set([...selected].filter((id) => String(id) !== archivedId)),
      );
      setReminderSelected((selected) =>
        new Set([...selected].filter((id) => String(id) !== archivedId)),
      );
      setUploadSelected((selected) =>
        new Set([...selected].filter((id) => String(id) !== archivedId)),
      );
      setDashboardRefreshVersion((version) => version + 1);

      showNotify("success", "Archived successfully!");
    } catch (e: any) {
      console.error("Archive failed", e);
      showNotify(
        "error",
        e?.response?.data?.detail ?? "Failed to archive bundle",
      );
    } finally {
      setConfirmArchive({ open: false, source: null, loading: false });
    }
  };

  const openConsentBundle = async (bundleId: string | number) => {
    if (openingConsentBundleId) return;

    setOpeningConsentBundleId(String(bundleId));
    let didNavigate = false;
    const navigateToBundle = (path: string) => {
      didNavigate = true;
      navigate(path);
    };

    await new Promise<void>((resolve) => {
      if (typeof window.requestAnimationFrame === "function") {
        window.requestAnimationFrame(() => resolve());
      } else {
        window.setTimeout(resolve, 0);
      }
    });

    try {
      const res = await axios.get(`/consent_bundle/${bundleId}`, {
        timeout: 0,
      });

      const data = res.data?.consent_bundle ?? res.data;

      console.log("Consent bundle data:", data);

      const rawStatus = data?.status;

      if (typeof rawStatus !== "string" || !rawStatus.trim()) {
        toast.error("No status found for this consent bundle");
        return;
      }

      const status = rawStatus.trim().toLowerCase();

      if (["completed", "signed", "expired"].includes(status)) {
        navigateToBundle(`/summary-new/${bundleId}`);
        return;
      }

      if (status === "draft") {
        const treatmentPlanId = getDraftTreatmentPlanId(data);
        let audioRecordData: any = null;
        let audioIsProcessing = false;

        sessionStorage.removeItem(DRAFT_AUDIO_RECORD_STORAGE_KEY);

        if (treatmentPlanId) {
          try {
            const base = process.env.REACT_APP_BASE_URL_V2;
            const endpoint = base
              ? `${base.replace(/\/+$/, "")}/audio-recording/get_audio_record_data/${encodeURIComponent(
                  treatmentPlanId,
                )}`
              : `/audio-recording/get_audio_record_data/${encodeURIComponent(
                  treatmentPlanId,
                )}`;
            const audioResponse = await axios.get(endpoint, {
              headers: { Accept: "application/json" },
              timeout: 0,
            });

            audioRecordData = audioResponse.data;
            audioIsProcessing = hasRecordingInProgress(
              toTranscriptionRow(unwrapAudioRecordData(audioRecordData), 0),
            );
          } catch (audioStatusError) {
            console.warn(
              "Could not check the draft transcription status before opening:",
              audioStatusError,
            );
          }
        }

        if (!audioRecordData) {
          const matchingRecording = transcriptionRows.find(
            (row) =>
              String(row.consentBundleId ?? "") === String(bundleId) ||
              (treatmentPlanId &&
                String(row.treatmentPlanId ?? "") === treatmentPlanId),
          );

          if (matchingRecording) {
            audioRecordData = matchingRecording.raw;
            audioIsProcessing = hasRecordingInProgress(matchingRecording);
          }
        }

        const activeTreatmentData = audioRecordData
          ? { ...data, audio_record_data: audioRecordData }
          : data;

        sessionStorage.setItem(
          "activeTreatmentData",
          JSON.stringify(activeTreatmentData),
        );

        const draftParams = new URLSearchParams({ step: "1" });

        if (audioRecordData) {
          sessionStorage.setItem(
            DRAFT_AUDIO_RECORD_STORAGE_KEY,
            JSON.stringify(audioRecordData),
          );
        }

        if (audioIsProcessing) {
          draftParams.set("transcription", "true");
          draftParams.set("processing", "true");
          draftParams.set("consentBundleId", String(bundleId));
        }

        navigateToBundle(`/new-consentBundle?${draftParams.toString()}`);
        return;
      }

      if (
        status.includes("sent") ||
        status.includes("unsigned") ||
        status.includes("sign")
      ) {
        navigateToBundle(`/summary-new/${bundleId}`);
        return;
      }

      toast.error(`Cannot open ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬ this consent is ${rawStatus}`);
    } catch (err: any) {
      console.error("Failed to fetch consent bundle:", err);

      toast.error(
        err?.response?.data?.detail ??
          err?.response?.data?.message ??
          "Error loading consent bundle",
      );
    } finally {
      if (!didNavigate) {
        setOpeningConsentBundleId(null);
      }
    }
  };

  const openConsentSummary = (bundleId: string | number) => {
    navigate(`/summary-new/${bundleId}`);
  };

  const sendEmail = (_appointment: Appointment) => {
    toast.info("Email action is not available yet.");
  };

  const openRevertModal = (_appointment: Appointment) => {
    toast.info("Revert action is not available yet.");
  };

  // ---------- Renders ----------
  const waitingRoomSection = WAITING_ROOM_SECTIONS[activeTab];
  const WaitingRoomIcon = waitingRoomSection.icon;
  const Tabs = (
    <div className={`${styles.tabs} tutorial-tabs`}>
      {(Object.keys(WAITING_ROOM_SECTIONS) as TabKey[]).map((tab) => {
        const { label, icon: Icon } = WAITING_ROOM_SECTIONS[tab];
        return <button
          key={tab}
          type="button"
          aria-pressed={activeTab === tab}
          className={`${styles.tab} ${activeTab === tab ? styles.tabActive : ""}`}
          onClick={() => changeDashboardTab(tab)}
        >
          <Icon aria-hidden="true" />
          <span>{label}</span>
        </button>;
      })}
    </div>
  );

  const sendReminders = async (ids: number[]) => {
    if (!ids.length || isSendingReminders) return;

    try {
      setIsSendingReminders(true);
      const base = process.env.REACT_APP_BASE_URL_V2;
      if (!base) throw new Error("REACT_APP_BASE_URL_V2 is not set");
      const endpoint = `${base.replace(/\/+$/, "")}/dashboard/bulk_send_reminders`;

      await axios.post(
        endpoint,
        { consent_bundle_ids: ids },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      setShowConfirmModal(false);
      toast.success("Reminders sent successfully!");
    } catch (e: any) {
      console.error("Send reminders failed", e);
      toast.error(e?.response?.data?.detail ?? "Failed to send reminders");
    } finally {
      setIsSendingReminders(false);
    }
  };

  const downloadSelectedBundles = async () => {
    if (!selectedBundleIds.length || bulkActionsBusy) return;

    try {
      setBulkDownloading(true);

      const base = process.env.REACT_APP_BASE_URL_V2;
      if (!base) throw new Error("REACT_APP_BASE_URL_V2 is not set");

      const endpoint = `${base.replace(/\/+$/, "")}/dashboard/bulk_download`;

      const resp = await axios.post(
        endpoint,
        { consent_bundle_ids: selectedBundleIds },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          responseType: "blob",
        },
      );

      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "consent_bundles.zip");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setActiveSelection(new Set());

      showNotify("success", "Downloaded successfully!");
    } catch (e: any) {
      console.error("Bulk download failed", e);
      showNotify(
        "error",
        e?.response?.data?.detail ?? "Failed to download bundles",
      );
    } finally {
      setBulkDownloading(false);
    }
  };

  const doBulkArchiveUpload = async () => {
    if (isBulkArchiving || selectedUploadArchiveIds.length === 0) return;
    const ids = selectedUploadArchiveIds;

    try {
      setIsBulkArchiving(true);
      showNotify("archive", "Archiving...");

      const base = process.env.REACT_APP_BASE_URL_V2;
      if (!base) throw new Error("REACT_APP_BASE_URL_V2 is not set");

      const endpoint = `${process.env.REACT_APP_BASE_URL_V2}dashboard/bulk_archive`;

      await axios.put(
        endpoint,
        { consent_bundle_ids: ids },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );

      setUploadRows((prev) =>
        prev.filter((r) => !ids.includes(Number(r.raw?.id ?? r.id))),
      );

      setUploadSelected(new Set());
      setDashboardRefreshVersion((version) => version + 1);

      showNotify("success", "Archived successfully!");
    } catch (e: any) {
      console.error("Bulk archive failed", e);
      showNotify(
        "error",
        e?.response?.data?.detail ?? "Failed to archive bundles",
      );
    } finally {
      setIsBulkArchiving(false);
    }
  };

  // ---- Notification system ----
  type NotifyType = "archive" | "download" | "success" | "error";

  const [notify, setNotify] = useState<{
    show: boolean;
    type: NotifyType;
    message: string;
  }>({
    show: false,
    type: "success",
    message: "",
  });

  const showNotify = (type: NotifyType, msg: string) => {
    if (type === "success") {
      setNotify({ show: false, type, message: "" });
      toast.success(msg);
      return;
    }

    setNotify({ show: true, type, message: msg });
    setTimeout(
      () => setNotify({ show: false, type: "success", message: "" }),
      2000,
    );
  };

  useGuestCheckoutRedirect();

  const handleTodayRowClick = (a) => {
    const bundleId = Number(a.bundleId ?? a.id);

    if (a.consentStatus === "EMAIL_BOUNCED") {
      toast.error("Cannot open ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬ email bounced");
      return;
    }

    if (a.consentStatus === "NOT_SIGNED") {
      openConsentBundle(bundleId);
      return;
    }

    if (a.consentStatus === "SIGNED") {
      openConsentSummary(bundleId);
      return;
    }

    toast.error(`Cannot open ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬ this consent is ${a.consentStatus}`);
  };

  useEffect(() => {
    let cancelled = false;
    const searchTerm = patientQuery.trim();
    setPatientSearchError("");

    if (!searchTerm || topView !== "waiting-room") {
      setPatientOptions([]);
      setLoadingPatients(false);
      return;
    }
    if (selectedPatientId) {
      setLoadingPatients(false);
      return;
    }

    setPatientOptions([]);
    setLoadingPatients(true);
    const delay = setTimeout(async () => {
      try {
        const base = process.env.REACT_APP_BASE_URL;
        const endpoint = `${base}patient`;

        const resp = await axios.get(endpoint, {
          params: { query: searchTerm },
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        const arr = Array.isArray(resp.data)
          ? resp.data
          : Array.isArray(resp.data?.data) ? resp.data.data : [];

        const formatted: DashboardPatientOption[] = arr.map((p) => ({
          id: Number(p.dentally_id),
          label: `${p.first_name?.trim() || ""} ${p.last_name?.trim() || ""}`.trim(),
          raw: p,
        })).filter((p) => Number.isSafeInteger(p.id) && p.id > 0 && p.label);

        if (!cancelled) setPatientOptions(formatted);
      } catch (err) {
        if (!cancelled) setPatientSearchError("Patient search is unavailable.");
      } finally {
        if (!cancelled) setLoadingPatients(false);
      }
    }, 300);

    return () => { cancelled = true; clearTimeout(delay); };
  }, [patientQuery, selectedPatientId, topView, patientSearchAttempt]);

  const toggleAllSelectAllTab = () => {
    const newSet = new Set<string>();
    if (!filteredAll.every((row) => selectedAllTab.has(row.id))) {
      filteredAll.forEach((a) => newSet.add(a.id));
    }
    setSelectedAllTab(newSet);
  };

  // select single row
  const toggleSingleSelectAllTab = (id) => {
    const copy = new Set(selectedAllTab);
    if (copy.has(id)) copy.delete(id);
    else copy.add(id);
    setSelectedAllTab(copy);
  };

  // SINGLE download
  const downloadSingleAll = async (id) => {
    try {
      setDownloadingIds((prev) => new Set(prev).add(id));

      const url = `/consent_bundle/${id}/download_full_consent_bundle_report`;

      const resp = await axios.get(url, { responseType: "blob" });

      const blobUrl = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `consent_bundle_${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error("Failed to download file");
    } finally {
      setDownloadingIds((prev) => {
        const s = new Set(prev);
        s.delete(id);
        return s;
      });
    }
  };

  // --- Fetch interaction log ---
  const openQuickView = async (id) => {
    const history = histories[String(id)];
    if (!history || history.status === "loading" || history.status === "empty") return;

    setQuickViewId(id);
    setQuickViewOpen(true);
    setQuickViewError("");

    if (history.status === "ready") {
      setInteractionLog(history.log);
      setQuickViewLoading(false);
      return;
    }

    setQuickViewLoading(true);
    setInteractionLog({});

    try {
      const endpoint = `/consent_bundle/${id}/interaction_log`;

      const res = await axios.get(endpoint);

      const log = parseInteractionLog(res.data);
      rememberHistory(id, log);
      if (Object.keys(log).length === 0) {
        setQuickViewOpen(false);
        toast.info("No interaction history for this record yet.");
        return;
      }
      setInteractionLog(log);
    } catch (err) {
      setQuickViewError("Failed to load interaction log");
    } finally {
      setQuickViewLoading(false);
    }
  };

  const canShowQr = (status?: string) => {
    if (!status) return false;
    const s = status.toLowerCase();
    return s.includes("sign") || s === "completed";
  };

  const TopDashboardToggle = (
    <div className={styles.topDashboardToggle}>
       <button
        type="button"
        aria-pressed={topView === "transcription"}
        className={`${styles.topDashboardToggleBtn} ${styles.topDashboardToggleAi} ${
          topView === "transcription"
            ? styles.topDashboardToggleActiveAi
            : ""
        }`}
        onClick={() => setTopView("transcription")}
      >
        <MdAutoAwesome className={styles.topDashboardAiIcon} />
        <span>AI transcription review <AiBetaBadge /></span>
        <strong>{transcriptionRows.length}</strong>
      </button>

      <button
        type="button"
        aria-pressed={topView === "waiting-room"}
        className={`${styles.topDashboardToggleBtn} ${
          topView === "waiting-room" ? styles.topDashboardToggleActive : ""
        }`}
        onClick={() => setTopView("waiting-room")}
      >
        <FiMonitor />
        <span>Waiting Room</span>
        <strong>{totalAll || total || todayAppts.length || 0}</strong>
      </button>
    </div>
  );

  const retryRecordingStage = async (
    row: TranscriptionRow,
    stage: RetryableRecordingStage,
  ) => {
    if (!row.uploadSessionId) {
      toast.error("Retry unavailable: upload session ID is missing.");
      return;
    }

    const retryKey = `${row.id}:${stage}`;

    if (retryingRecordingStage === retryKey) return;

    try {
      setRetryingRecordingStage(retryKey);

      const base = process.env.REACT_APP_BASE_URL_V2;
      if (!base) {
        throw new Error("REACT_APP_BASE_URL_V2 is not set");
      }

      const retryPath =
        stage === "transcript"
          ? "generate_transcript_and_patient_note"
          : "generate_patient_note";
      const endpoint = `${base.replace(/\/+$/, "")}/audio-recording/${retryPath}/${encodeURIComponent(
        row.uploadSessionId,
      )}`;

      await axios.post(endpoint);

      setTranscriptionRows((currentRows) =>
        currentRows.map((currentRow) => {
          if (currentRow.id !== row.id) return currentRow;

          if (stage === "transcript") {
            return {
              ...currentRow,
              transcriptStatus: "processing",
              patientNoteStatus: "processing",
            };
          }

          return {
            ...currentRow,
            patientNoteStatus: "processing",
          };
        }),
      );

      toast.success(
        stage === "transcript"
          ? "Transcript and patient-note generation restarted."
          : "Patient-note generation restarted.",
      );
    } catch (error: any) {
      console.error(`Failed to retry ${stage} generation`, error);

      toast.error(
        error?.response?.data?.detail ??
          error?.response?.data?.message ??
          error?.message ??
          "Failed to restart generation.",
      );
    } finally {
      setRetryingRecordingStage(null);
    }
  };

  const openTranscriptionReview = async (row: TranscriptionRow) => {
    const bundleId = row.consentBundleId;

    if (!isPatientNoteReady(row.patientNoteStatus)) {
      toast.info("The patient note is not generated yet.");
      return;
    }

    console.log("Review & Edit clicked:", {
      consentBundleId: bundleId,
      treatmentPlanId: row.treatmentPlanId,
      transcriptStatus: row.transcriptStatus,
      row,
    });

    if (!bundleId) {
      navigate("/new-consentBundle?step=1&transcription=true");
      return;
    }

    try {
      setReviewingTranscriptionId(row.id);

      const token =
        axios.defaults.headers.common.Authorization ??
        axios.defaults.headers.common.authorization;

      const res = await axios.get(
        `/consent_bundle/${bundleId}`,
        {
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: String(token) } : {}),
          },
        },
      );

      const data = res.data?.consent_bundle ?? res.data;

      if (!isDraftConsentBundle(data)) {
        setTranscriptionRows((rows) => rows.filter((recording) => recording.consentBundleId !== bundleId));
        toast.info("Only draft consent bundles can be reviewed from the transcription dashboard.");
        return;
      }

      sessionStorage.setItem("activeTreatmentData", JSON.stringify(data));
      sessionStorage.setItem("openedFromTranscriptionReview", "true");

      const reviewParams = new URLSearchParams({
        step: "1",
        consentBundleId: String(bundleId),
        transcription: "true",
        from: "dashboard",
      });

      if (row.treatmentPlanId) {
        reviewParams.set("treatmentPlanId", String(row.treatmentPlanId));
      }
      if (row.uploadSessionId) {
        reviewParams.set("uploadSessionId", row.uploadSessionId);
      }

      const patientNoteStatus = normalizeRecordingStatus(
        row.patientNoteStatus,
      );

      if (patientNoteStatus) {
        reviewParams.set("patientNoteStatus", patientNoteStatus);
      }

      if (patientNoteStatus === "edited") {
        reviewParams.set("focus", "ai-patient-letter-generator");
      }

      navigate(
        `/new-consentBundle?${reviewParams.toString()}`,
      );
    } catch (err: any) {
      console.error("Failed to load consent bundle:", err);

      toast.error(
        err?.response?.data?.detail ??
          err?.response?.data?.message ??
          "Failed to load consent bundle",
      );
    } finally {
      setReviewingTranscriptionId(null);
    }
  };

  const TranscriptionReviewPage = (
    <section id="audio-recordings-transcription-review" className={styles.transcriptionPanel}>
      <header className={`${styles.header} ${styles.transcriptionHeader}`}>
        <div className={styles.transcriptionHeadingGroup}>
          <div className={styles.transcriptionAiMark} aria-hidden="true">
            <FiCpu />
          </div>

          <div>
            <h1 className={styles.transcriptionTitle}>
              AI transcription <AiBetaBadge />
            </h1>
            <div className={styles.transcriptionSubhead}>
              <span>
                Recordings, transcripts and patient notes.
              </span>
            </div>
          </div>
        </div>

        <div className={styles.transcriptionTotal}>
          <strong>{hasTranscriptionFilters ? filteredTranscriptionRows.length : transcriptionRows.length}</strong>
          <span>{hasTranscriptionFilters ? `of ${transcriptionRows.length} recordings` : "recordings"}</span>
        </div>
      </header>

      <TranscriptionFilters
        patientQuery={transcriptionPatientQuery}
        clinician={transcriptionClinician}
        patients={transcriptionPatientOptions}
        clinicians={transcriptionClinicianOptions}
        loading={loadingTranscriptions}
        onPatientChange={setTranscriptionPatientQuery}
        onClinicianChange={setTranscriptionClinician}
      />

      <div className={styles.transcriptionCard}>
        <div className={styles.transcriptionCardHeader}>
          <div className={styles.transcriptionCardHeading}>
            <FiActivity aria-hidden="true" />
            <div>
              <h2>AI processing queue</h2>
              <p>Review recording, transcript and patient-note statuses.</p>
            </div>
          </div>

          <div
            className={`${styles.transcriptionPollStatus} ${
              transcriptionProcessingCount > 0
                ? styles.transcriptionPollProcessing
                : transcriptionFailedCount > 0
                  ? styles.transcriptionPollError
                  : styles.transcriptionPollReady
            }`}
          >
            <span>{transcriptionPollLabel}</span>
            <small>{transcriptionPollMeta}</small>
          </div>
        </div>

        <div className={styles.transcriptionTable} role="region" aria-label="Transcription recordings">
          <div
            className={`${styles.transcriptionGrid} ${styles.transcriptionTableHead}`}
          >
            <div>Treatment</div>
            <div>Patient</div>
            <div>Clinician</div>
            <div>Recorded at</div>
            <div>Consent</div>
            <div>Status</div>
            <div>Audio</div>
            <div>Transcript</div>
            <div>Patient note</div>
            <div>Actions</div>
          </div>

          <div className={styles.transcriptionTableBody}>
            {loadingTranscriptions && (
              <div className={styles.transcriptionEmptyRow}>
                Loading audio recordings...
              </div>
            )}

            {errorTranscriptions && !loadingTranscriptions && (
              <div className={styles.transcriptionErrorRow}>
                {errorTranscriptions}
              </div>
            )}

            {!loadingTranscriptions &&
              !errorTranscriptions &&
              transcriptionRows.length === 0 && (
                <div className={styles.transcriptionNoDataRow}>
                  <div className={styles.transcriptionNoDataBox}>
                    <div className={styles.transcriptionNoDataIcon}>
                      <MdAutoAwesome />
                    </div>
                    <h3>No audio recordings found</h3>
                    <p>AI-ready recordings will appear here.</p>
                  </div>
                </div>
              )}

            {!loadingTranscriptions && !errorTranscriptions && transcriptionRows.length > 0 && filteredTranscriptionRows.length === 0 && (
              <div className={styles.transcriptionNoDataRow}>
                <div className={styles.transcriptionNoDataBox}>
                  <div className={styles.transcriptionNoDataIcon}><FiSearch aria-hidden="true" /></div>
                  <h3>No matching recordings</h3>
                  <p>Try another patient or clinician.</p>
                </div>
              </div>
            )}

            {!loadingTranscriptions &&
              !errorTranscriptions &&
              filteredTranscriptionRows.map((row) => {
                const patientNoteReady = isPatientNoteReady(
                  row.patientNoteStatus,
                );
                return (
                  <div
                    key={row.id}
                    className={`${styles.transcriptionGrid} ${styles.transcriptionRow}`}
                  >

                    <div>
                      <span className={styles.transcriptionIdBadge}>
                        #{row.treatmentPlanId ?? "-"}
                      </span>
                    </div>

                    <div className={styles.transcriptionPersonCell}>
                      <strong>{row.patientName}</strong>
                    </div>

                    <div className={styles.transcriptionPersonCell}>
                      {row.clinicianName}
                    </div>

                    <div className={styles.transcriptionRecordedAt}>
                      <FiClock />
                      <span>{fmtSafeDateTime(row.recordedAt)}</span>
                    </div>

                    <div>
                      <span className={styles.transcriptionBundleBadge}>
                        #{row.consentBundleId ?? "-"}
                      </span>
                    </div>

                    <div>
                      <ConsentStatusPill record={{ status: row.consentStatus }} />
                    </div>

                    <div>
                      <span
                        className={`${styles.transcriptionStatusBadge} ${
                          styles[getStageBadgeClass(row.audioStatus)]
                        }`}
                      >
                        <FiMic aria-hidden="true" />
                        {formatRecordingStatus(row.audioStatus, "Audio uploaded")}
                      </span>
                    </div>

                    <div>
                      <span
                        className={`${styles.transcriptionReviewBadge} ${
                          styles[getStageBadgeClass(row.transcriptStatus)]
                        }`}
                      >
                        <FiFileText aria-hidden="true" />
                        {formatRecordingStatus(
                          row.transcriptStatus,
                          "Transcript Generated",
                        )}
                        {isStageFailed(row.transcriptStatus) && (
                          <button
                            type="button"
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              retryRecordingStage(row, "transcript");
                            }}
                            disabled={
                              retryingRecordingStage ===
                              `${row.id}:transcript`
                            }
                            title="Retry transcript and patient-note generation"
                            style={{
                              marginLeft: 8,
                              border: "1px solid currentColor",
                              borderRadius: 999,
                              padding: "3px 7px",
                              background: "transparent",
                              color: "inherit",
                              font: "inherit",
                              fontSize: 11,
                              fontWeight: 700,
                              lineHeight: 1,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              cursor: "pointer",
                            }}
                          >
                            <FiRotateCcw />
                            {retryingRecordingStage === `${row.id}:transcript`
                              ? "Retrying..."
                              : "Retry"}
                          </button>
                        )}
                      </span>
                    </div>

                    <div>
                      <span
                        className={`${styles.transcriptionValueBadge} ${
                          styles[getStageBadgeClass(row.patientNoteStatus)]
                        }`}
                      >
                        <FiAlignLeft aria-hidden="true" />
                        {formatRecordingStatus(
                          row.patientNoteStatus,
                          "Patient note Generated",
                        )}
                        {isStageFailed(row.patientNoteStatus) && (
                          <button
                            type="button"
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              retryRecordingStage(row, "patient-note");
                            }}
                            disabled={
                              retryingRecordingStage ===
                              `${row.id}:patient-note`
                            }
                            title="Retry patient-note generation"
                            style={{
                              marginLeft: 8,
                              border: "1px solid currentColor",
                              borderRadius: 999,
                              padding: "3px 7px",
                              background: "transparent",
                              color: "inherit",
                              font: "inherit",
                              fontSize: 11,
                              fontWeight: 700,
                              lineHeight: 1,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              cursor: "pointer",
                            }}
                          >
                            <FiRotateCcw />
                            {retryingRecordingStage ===
                            `${row.id}:patient-note`
                              ? "Retrying..."
                              : "Retry"}
                          </button>
                        )}
                      </span>
                    </div>

                    <div className={styles.transcriptionActions}>
                      <button
                        type="button"
                        className={styles.transcriptionReviewBtn}
                        disabled={
                          !patientNoteReady ||
                          reviewingTranscriptionId === row.id
                        }
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          openTranscriptionReview(row);
                        }}
                        title={
                          patientNoteReady
                            ? row.consentBundleId
                              ? "Review and edit consent bundle"
                              : "Consent bundle ID is missing. Open new consent bundle."
                            : "The patient note is not generated yet"
                        }
                      >
                        <FiEdit3 aria-hidden="true" />
                        {reviewingTranscriptionId === row.id
                          ? "Loading..."
                          : patientNoteReady
                            ? "Review & Edit"
                            : "Not ready"}
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </section>
  );


  
  return (
    <>
      <Header />
      <div className={`${styles.wrap} ${topView === "transcription" ? styles.aiWorkspace : styles.waitingWorkspace}`}>
        {TopDashboardToggle}
        <Tooltip title="Show tutorials" arrow placement="left">
          <button
            type="button"
            className={styles.tutorialHelpButton}
            onClick={() => setShowDashboardTutorial(true)}
            aria-label="Show dashboard tutorials"
          >
            <FiInfo />
          </button>
        </Tooltip>

        {topView === "transcription" ? (
          TranscriptionReviewPage
        ) : (
          <>
            <header className={`${styles.header} ${styles.waitingRoomHeader}`}>
              <div key={activeTab} className={styles.waitingRoomHeading}>
                <span className={styles.waitingRoomMark} aria-hidden="true"><WaitingRoomIcon /></span>
                <h1 className={styles.title}>{waitingRoomSection.title}</h1>
              </div>
              <div className={styles.date}>
                <FiCalendar aria-hidden="true" />
                <span>{todayLong}</span>
              </div>
            </header>

            {/* Filters */}
            <section
              className={`${styles.filters} tutorial-filters tutorial-dashboard-filters`}
              aria-label="Appointment filters"
            >
              <div className={styles.filtersRow}>
                {/* search patient */}
                <div className={`${styles.filter} ${styles.patientSearchFilter} tutorial-patient-search`}>
                  <label htmlFor="dashboard-patient-search">Search patients</label>
                  <div className={styles.filterInputWrapper} data-active={Boolean(selectedPatientId)}>
                    <Autocomplete
                      id="dashboard-patient-search"
                      options={patientOptions}
                      loading={loadingPatients}
                      loadingText="Searching patients..."
                      forcePopupIcon={false}
                      clearText="Clear patient search"
                      clearOnBlur={false}
                      autoHighlight
                      openOnFocus
                      filterOptions={(options) => options}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      componentsProps={{ paper: { className: styles.patientSearchPaper } }}
                      value={
                        selectedPatientId
                          ? patientOptions.find(
                              (p) => p.id === selectedPatientId,
                            ) || { id: selectedPatientId, label: patientQuery, raw: {} }
                          : null
                      }
                      inputValue={patientQuery}
                      getOptionLabel={(opt) => opt?.label || ""}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setSelectedPatientId(newValue.id);
                          setPatientQuery(newValue.label);
                        } else {
                          clearPatientSearch();
                        }

                        resetFilterPages();
                      }}
                      onInputChange={(event, newInputValue, reason) => {
                        if (reason === "clear") {
                          clearPatientSearch();
                          return;
                        }
                        if (reason === "blur" || reason === "reset") {
                          return;
                        }
                        setSelectedPatientId(null);
                        setPatientQuery(newInputValue);
                        resetFilterPages();
                      }}
                      noOptionsText={patientSearchError || (patientQuery.trim() ? "No patients found" : "Type a patient name to search")}
                      renderOption={(props, option) => <li {...props} key={option.id}>
                        <span className={styles.patientSearchAvatar} aria-hidden="true"><FiUser /></span>
                        <div className={styles.patientSearchDetails}>
                          <strong>{option.label}</strong>
                          {option.raw.date_of_birth && <small>DOB {fmtSafeDate(option.raw.date_of_birth)}</small>}
                        </div>
                      </li>}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search by patient"
                          variant="outlined"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: <FiSearch className={styles.patientSearchIcon} aria-hidden="true" />,
                            endAdornment: <>
                              {loadingPatients && <CircularProgress className={styles.patientSearchSpinner} size={15} color="inherit" aria-label="Searching patients" />}
                              {patientQuery && !selectedPatientId && <ClearButton label="Clear patient search" onClear={clearPatientSearch} />}
                              {params.InputProps.endAdornment}
                            </>,
                            style: {
                              height: "40px",
                              paddingLeft: "12px",
                              paddingTop: 0,
                              paddingBottom: 0,
                              fontWeight: 500,
                            },
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root fieldset": {
                              border: "none",
                            },
                            "& input::placeholder": {
                              color: "#697587",
                              opacity: 1,
                            },
                          }}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className={`${styles.filter} ${styles.clinicianFilter} tutorial-clinician-filter`}>
                  <label id="dashboard-clinician-filter-label" htmlFor="dashboard-clinician-filter">Clinician</label>
                  <DashboardFilterSelect
                    id="dashboard-clinician-filter"
                    label="Clinician"
                    value={clinician}
                    options={[{ value: "all", label: "All clinicians" }, ...clinicians]}
                    fallbackLabel="Selected clinician"
                    onChange={(value) => { setClinician(value); resetFilterPages(); }}
                    onClear={clearClinicianFilter}
                  />
                </div>

                <div className={`${styles.filter} ${styles.statusFilter} tutorial-status-filter`}>
                  <label id="dashboard-status-filter-label" htmlFor="dashboard-status-filter">Status</label>
                  <DashboardFilterSelect
                    id="dashboard-status-filter"
                    label="Status"
                    value={statusFilter}
                    options={[
                      { value: "all", label: "All statuses" },
                      ...(activeTab !== "reminders" ? [{ value: "Draft", label: "Draft" }] : []),
                      { value: "Sent For Signing", label: "Sent for signing" },
                      { value: "Completed", label: "Completed" },
                      { value: "Archived", label: "Archived" },
                    ]}
                    onChange={(value) => {
                        setStatusFilter(value);
                        setConsent("all");
                        setConsentStatusParam(value === "all" ? undefined : value);
                        resetFilterPages();
                    }}
                    onClear={clearStatusFilter}
                  />
                </div>

                <div className={styles.filterActions}>
                  <details
                    className={styles.filterOptions}
                    onBlur={(event) => {
                      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) event.currentTarget.open = false;
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") {
                        event.currentTarget.open = false;
                        event.currentTarget.querySelector("summary")?.focus();
                      }
                    }}
                  >
                    <summary aria-label="Filter options" title="Filter options"><FiSliders aria-hidden="true" /></summary>
                    <div className={styles.filterOptionsPanel}>
                      <label className={styles.toggleLabel}>
                        <span className={styles.toggleText}>Reset on tab change</span>
                        <button
                          type="button"
                          role="switch"
                          aria-label="Clear filters on tab change"
                          aria-checked={autoClearFilters}
                          className={`${styles.toggleSwitch} ${autoClearFilters ? styles.toggleOn : ""}`}
                          onClick={() => setAutoClearFilters((current) => !current)}
                        >
                          <span className={styles.toggleKnob} aria-hidden="true" />
                        </button>
                      </label>
                    </div>
                  </details>
                </div>
              </div>
              {hasActiveFilters && <div className={styles.filtersFooter}>
                <div className={styles.activeFilters} role="group" aria-label="Active appointment filters">
                  {selectedPatientId && <span className={styles.activeFilterPill}>
                    <FiUser aria-hidden="true" /><span>{patientQuery || "Selected patient"}</span>
                    <ClearButton label="Remove patient filter" onClear={clearPatientSearch} />
                  </span>}
                  {clinician !== "all" && <span className={styles.activeFilterPill}>
                    <span>Clinician: {clinicians.find((option) => option.value === clinician)?.label || "Selected clinician"}</span>
                    <ClearButton label="Remove clinician filter" onClear={clearClinicianFilter} />
                  </span>}
                  {statusFilter !== "all" && <span className={styles.activeFilterPill}>
                    <span>{statusFilter === "Sent For Signing" ? "Sent for signing" : statusFilter}</span>
                    <ClearButton label="Remove status filter" onClear={clearStatusFilter} />
                  </span>}
                  {query && <span className={styles.activeFilterPill}>
                    <span>Search: {query}</span>
                    <ClearButton label="Remove text filter" onClear={() => { setQuery(""); resetFilterPages(); }} />
                  </span>}
                  {patientQuery.trim() && !selectedPatientId && <span className={patientSearchError ? styles.filterError : styles.filterHint} role={patientSearchError ? "alert" : undefined}>
                    {patientSearchError || (loadingPatients ? "Finding patients..." : "Choose a patient from the suggestions.")}
                    {patientSearchError && <button type="button" onClick={() => setPatientSearchAttempt((attempt) => attempt + 1)}>Retry</button>}
                  </span>}
                </div>
                <button type="button" className={styles.clearFiltersButton} onClick={clearDashboardFilters} aria-label="Clear all filters">
                  <FiRotateCcw aria-hidden="true" />Clear all
                </button>
              </div>}
            </section>

            {/* Card + Tabs */}
            <section className={styles.card}>
              {Tabs}

              {selectedBulkRows.length > 0 && <div className={styles.bulkActionsBar} role="group" aria-label="Bulk actions">
                <span className={styles.bulkSelectionCount} aria-live="polite">
                  {selectedBulkRows.length} of {bulkRows.length} selected
                </span>
                <div className={styles.bulkActionButtons}>
                  {activeTab === "upload" && <button
                    type="button"
                    className={styles.btnSecondary}
                    disabled={bulkActionsBusy || selectedBundleIds.length === 0 || selectedUploadArchiveIds.length === 0}
                    onClick={doBulkArchiveUpload}
                  >
                    <FiArchive aria-hidden="true" />
                    {isBulkArchiving ? "Archiving..." : "Archive selected"}
                  </button>}
                  <button
                    type="button"
                    className={styles.btnSecondary}
                    disabled={bulkActionsBusy || selectedBundleIds.length === 0}
                    aria-busy={bulkDownloading}
                    onClick={downloadSelectedBundles}
                  >
                    <FiDownload aria-hidden="true" />
                    {bulkDownloading ? "Downloading..." : "Download selected"}
                  </button>
                  <button
                    type="button"
                    className={styles.btnPrimary}
                    disabled={bulkActionsBusy || selectedBundleIds.length === 0 || selectionIncludesDraft}
                    title={selectionIncludesDraft ? "Draft consent bundles cannot receive reminders" : undefined}
                    onClick={() => {
                      setPendingReminderIds(selectedBundleIds);
                      setShowConfirmModal(true);
                    }}
                  >
                    <FiSend aria-hidden="true" /> Send reminders
                  </button>
                </div>
              </div>}

              {/* Wrap tab content to animate on tab change */}
              <div key={activeTab} className={styles.animIn}>
                {/* ---- Today / Waiting room ---- */}
                {activeTab === "today" && (
                  <>
                    <div role="table" aria-label="Today?s consent records" className={`${styles.table} tutorial-table`}>
                      <div role="row" className={`${styles.grid} ${styles.tableHead}`}>
                        <div role="columnheader"><input type="checkbox" aria-label="Select all today's records"
                          checked={todayFiltered.length > 0 && todayFiltered.every((row) => todaySelected.has(row.id))}
                          onChange={() => setTodaySelected(todayFiltered.every((row) => todaySelected.has(row.id)) ? new Set() : new Set(todayFiltered.map((row) => row.id)))} /></div>
                        <div role="columnheader">Patient name</div>
                        {sortHeader("treatmentValue", "Treatment value")}
                        <div role="columnheader">Date of Birth</div>
                        <div role="columnheader">Clinician</div>
                        <div role="columnheader">Status</div>
                        {sortHeader("nextAppointment", "Next appointment")}
                        <div role="columnheader">Email</div>
                        <div role="columnheader">Actions</div>
                      </div>

                      <div role="rowgroup" className={styles.tableBody}>
                        {loadingToday && (
                          <div className={styles.row}>
                            <div
                              role="cell" className={styles.cell}
                              style={{ gridColumn: "1 / -1" }}
                            >
                              Loading waiting roomÃƒâ€šÃ‚Â¦
                            </div>
                          </div>
                        )}

                        {errorToday && !loadingToday && (
                          <div className={styles.row}>
                            <div
                              role="cell" className={styles.cell}
                              style={{ gridColumn: "1 / -1", color: "crimson" }}
                            >
                              {errorToday}
                            </div>
                          </div>
                        )}

                        {!loadingToday &&
                          !errorToday &&
                          todayFiltered.length === 0 && (
                            <div className={styles.rowSoft}>
                              <div
                                role="cell" className={styles.cell}
                                style={{ gridColumn: "1 / -1", opacity: 0.7 }}
                              >
                                No data matches your filters.
                              </div>
                            </div>
                          )}

                        {!loadingToday &&
                          !errorToday &&
                          todayFiltered.map((a) => {
                            return (
                              <div
                                role="row" className={`${styles.row} ${styles.grid}`}
                                key={a.id}
                                onClick={() => handleTodayRowClick(a)}
                                style={{ cursor: "pointer" }}
                              >
                                <div role="cell" className={styles.cell}>
                                  <input type="checkbox" aria-label={`Select ${a.patient}`} checked={todaySelected.has(a.id)}
                                    onClick={(event) => event.stopPropagation()}
                                    onChange={() => setTodaySelected((selected) => { const next = new Set(selected); next.has(a.id) ? next.delete(a.id) : next.add(a.id); return next; })} />
                                </div>

                                <div role="cell" className={styles.cell}>
                                  <div className={styles.patient}>
                                    <span className={styles.avatar}>
                                      {a.patient
                                        .split(" ")
                                        .map((n) => n[0])
                                        .slice(0, 2)
                                        .join("")
                                        .toUpperCase()}
                                    </span>
                                    <span className={styles.patientName}>
                                      {a.patient}
                                    </span>
                                  </div>
                                </div>

                                <div role="cell" className={styles.cell}><TreatmentValuePill record={a.raw} /></div>

                                <div role="cell" className={styles.cell}>
                                  <div className={styles.patient}>
                                    <span className={styles.patientName}>
                                      {fmtSafeDate(a.date_of_birth)}
                                    </span>
                                  </div>
                                </div>

                                <div role="cell" className={styles.cell}>{a.clinician}</div>
                                <div role="cell" className={styles.cell}><ConsentStatusPill record={a.raw} /></div>
                                <div role="cell" className={styles.cell}><NextAppointmentPill record={a.raw} /></div>
                                <div role="cell" className={styles.cell}>
                                  <div className={styles.iconWithNum} tabIndex={0}
                                    title={`Emails sent: ${a.emails}; SMS sent: ${a.texts}; Appointment time: ${a.time}`}>
                                    <FiMail /><span>{a.emails}</span>
                                  </div>
                                </div>

                                <div
                                  role="cell" className={`${styles.cell} ${styles.actions}`}
                                >
                                  <button
                                    className={`${styles.btn} ${styles.btnLight} tutorial-qr`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openQr(a);
                                    }}
                                  >
                                    <FiGrid /> QR Code
                                  </button>

                                  {a.consentStatus !== "SIGNED" ? (
                                    <button
                                      className={`${styles.btn} ${styles.btnOutlineBlue}`} aria-label="Send reminder"
                                      disabled={
                                        a.status?.toLowerCase() === "draft"
                                      }
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const id = Number(a.raw?.id ?? a.id);
                                        setPendingReminderIds([id]);
                                        setShowConfirmModal(true);
                                      }}
                                    >
                                      <FiSend /> Send reminder
                                    </button>
                                  ) : (
                                    <button
                                      className={`${styles.btn} ${styles.btnPill} ${styles.btnWhite}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        sendEmail?.(a);
                                      }}
                                    >
                                      Email
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                      <div className={styles.tableFooter}>
                        <div className={styles.pagerInfo}>
                          Page {page}  Per page
                          <select
                            value={perPage}
                            onChange={(e) => {
                              setPage(1);
                              setPerPage(parseInt(e.target.value, 10));
                            }}
                            style={{ marginLeft: 8 }}
                          >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                          </select>
                          <span style={{ marginLeft: 12 }}>Total: {total}</span>
                        </div>
                        <div className={styles.pagerBtns}>
                          <button
                            className={styles.btn}
                            disabled={page <= 1 || loadingToday}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                          >
                            Prev
                          </button>
                          <button
                            className={styles.btn}
                            disabled={
                              loadingToday ||
                              (todayAppts.length < perPage &&
                                page * perPage >= total)
                            }
                            onClick={() => setPage((p) => p + 1)}
                            style={{ marginLeft: 8 }}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                
                {activeTab === "all" && (
                  <>
                    <div role="table" aria-label="All consent records" className={`${styles.table} tutorial-dashboard-table`}>
                      {/* TABLE HEADER */}
                      <div
                        role="row" className={`${styles.gridRemAll} ${styles.tableHead}`}
                      >
                        <div role="columnheader">
                          <input
                            aria-label="Select all records"
                            type="checkbox"
                            checked={
                              filteredAll.length > 0 &&
                              filteredAll.every((row) => selectedAllTab.has(row.id))
                            }
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleAllSelectAllTab();
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>

                        <div role="columnheader">Patient name</div>
                        {sortHeader("treatmentValue", "Treatment value")}
                        <div role="columnheader">Date of Birth</div>
                        <div role="columnheader">Clinician</div>
                        <div role="columnheader">Status</div>
                        {sortHeader("nextAppointment", "Next appointment")}
                        <div role="columnheader">Email</div>
                        <div role="columnheader">Actions</div>
                      </div>

                      <div role="rowgroup" className={styles.tableBody}>
                        {/* LOADING */}
                        {loadingAll && (
                          <div
                            role="row" className={`${styles.rowSoft} ${styles.gridRemAll}`}
                          >
                            <div
                              role="cell" className={styles.cell}
                              style={{ gridColumn: "1 / -1" }}
                            >
                              Loading all appointments
                            </div>
                          </div>
                        )}

                        {/* ERROR */}
                        {errorAll && !loadingAll && (
                          <div
                            role="row" className={`${styles.rowSoft} ${styles.gridRemAll}`}
                          >
                            <div
                              role="cell" className={styles.cell}
                              style={{ gridColumn: "1 / -1", color: "crimson" }}
                            >
                              {errorAll}
                            </div>
                          </div>
                        )}

                        {/* ROWS */}
                        {!loadingAll &&
                          !errorAll &&
                          filteredAll.map((a) => (
                            <div
                              role="row" className={`${styles.rowSoft} ${styles.gridRemAll}`}
                              key={a.id}
                              onClick={() =>
                                openConsentBundle(a.raw?.id ?? a.id)
                              }
                              style={{ cursor: "pointer" }}
                            >
                              {/* Checkbox */}
                              <div
                                role="cell" className={styles.cell}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                {/* BIG CHECKBOX */}
                                <input
                                  aria-label={`Select ${a.patient}`}
                                  className={styles.checkboxLarge}
                                  type="checkbox"
                                  checked={selectedAllTab.has(a.id)}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    toggleSingleSelectAllTab(a.id);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />

                              </div>

                              {/* Patient */}
                              <div role="cell" className={styles.cell}>
                                <div className={styles.patient}>
                                  <div className={styles.avatar}>
                                    {a.patient
                                      ?.split(" ")
                                      .map((p) => p[0])
                                      .slice(0, 2)
                                      .join("")
                                      .toUpperCase()}
                                  </div>
                                  <span className={styles.patientName}>
                                    {a.patient}
                                  </span>
                                </div>
                              </div>

                              <div role="cell" className={styles.cell}><TreatmentValuePill record={a.raw} /></div>

                              {/* DOB */}
                              <div role="cell" className={styles.cell}>
                                {fmtSafeDate(a.date_of_birth)}
                              </div>

                              {/* Clinician */}
                              <div role="cell" className={styles.cell}>{a.clinician}</div>

                              {/* Status */}
                              <div
                                role="cell" className={`${styles.cell} tutorial-row-status`}
                              >
                                <ConsentStatusPill record={a.raw} />
                              </div>

                              <div role="cell" className={`${styles.cell} tutorial-next-appointment`}>
                                <NextAppointmentPill record={a.raw} />
                              </div>

                              {/* Email */}
                              <div role="cell" className={styles.cell}>
                                <div
                                  className={`${styles.iconWithNum} tutorial-communication-counts`}
                                  tabIndex={0}
                                  title={`Emails sent: ${a.emails}; SMS sent: ${a.texts}; Signed date: ${fmtSafeDate(a.signed_date)}`}
                                >
                                  <Tooltip title={a.patient_email?.trim() ? "Emails sent" : "No email"}>
                                    <span className={!a.patient_email?.trim() ? styles.noEmailIcon : undefined}><FiMail /></span>
                                  </Tooltip>
                                  <span>{a.emails}</span>
                                </div>
                              </div>

                              {/* ACTIONS */}
                              <div
                                role="cell" className={`${styles.cell} ${styles.actionsAll} tutorial-dashboard-actions`}
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  alignItems: "center",
                                }}
                              >
                                {canShowQr(a.status) && (
                                  <button
                                    className={`${styles.btn} ${styles.btnLight}`}
                                    title="QR Code"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openQr(a);
                                    }}
                                  >
                                    <FiGrid />
                                  </button>
                                )}

                                {/* Quick View */}
                                <QuickViewButton
                                  className={`${styles.btn} ${styles.btnLight} tutorial-quick-view`}
                                  history={histories[String(a.raw?.id ?? a.id)]}
                                  onOpen={() => openQuickView(a.raw?.id ?? a.id)}
                                >
                                  <FiEye />
                                </QuickViewButton>

                                {a.status?.toLowerCase() === "draft" &&
                                  a.versions?.length > 0 && (
                                    <Tooltip title="Revert to previous version">
                                      <button
                                        className={`${styles.btn} ${styles.btnOutlineBlue}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openRevertModal(a);
                                        }}
                                      >
                                        <FiRotateCcw />
                                      </button>
                                    </Tooltip>
                                  )}

                                <Tooltip title="Send reminder">
                                  <span>
                                    <button
                                      className={`${styles.btn} ${styles.btnOutlineBlue}`} aria-label="Send reminder"
                                      disabled={
                                        a.status?.toLowerCase() === "draft"
                                      }
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const id = Number(a.raw?.id ?? a.id);
                                        setPendingReminderIds([id]);
                                        setShowConfirmModal(true);
                                      }}
                                    >
                                      <FiSend />
                                    </button>
                                  </span>
                                </Tooltip>

                                {/* Download */}
                                <button
                                  className={`${styles.btn} ${styles.btnLight}`}
                                  title="Download"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    downloadSingleAll(a.raw?.id ?? a.id);
                                  }}
                                >
                                  {downloadingIds.has(a.id) ? (
                                    <span className={styles.spinner}></span>
                                  ) : (
                                    <>
                                      <FiDownload />
                                    </>
                                  )}
                                </button>

                                {/* Archive */}
                                <button
                                  className={`${styles.btn} ${styles.btnLight}`}
                                  title={isArchivedRecord(a) ? "Already archived" : "Archive"}
                                  disabled={isArchivedRecord(a) || isBulkArchiving}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    askArchive(a.id, a.patient, "all");
                                  }}
                                >
                                  <FiArchive />
                                </button>
                              </div>
                            </div>
                          ))}

                        {/* No data */}
                        {!loadingAll &&
                          !errorAll &&
                          filteredAll.length === 0 && (
                            <div
                              role="row" className={`${styles.rowSoft} ${styles.gridRemAll}`}
                            >
                              <div
                                role="cell" className={styles.cell}
                                style={{ gridColumn: "1 / -1", opacity: 0.7 }}
                              >
                                No data matches your filters.
                              </div>
                            </div>
                          )}
                      </div>
                    </div>

                    {/* Pagination */}
                    <div className={styles.tableFooter}>
                      <div className={styles.pagerInfo}>
                        Page {pageAll} Per page
                        <select
                          value={perPageAll}
                          onChange={(e) => {
                            setPageAll(1);
                            setPerPageAll(parseInt(e.target.value, 10));
                          }}
                          style={{ marginLeft: 8 }}
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                        </select>
                        <span style={{ marginLeft: 12 }}>
                          Total: {totalAll}
                        </span>
                      </div>

                      <div className={styles.pagerBtns}>
                        <button
                          className={styles.btn}
                          disabled={pageAll <= 1 || loadingAll}
                          onClick={() => setPageAll((p) => Math.max(1, p - 1))}
                        >
                          Prev
                        </button>
                        <button
                          className={styles.btn}
                          disabled={
                            loadingAll || pageAll * perPageAll >= totalAll
                          }
                          onClick={() => setPageAll((p) => p + 1)}
                          style={{ marginLeft: 8 }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </>
                )}
                {/* ---- Send reminder ---- */}
                {activeTab === "reminders" && (
                  <>
                    <div role="table" aria-label="Consent records" className={styles.table}>
                      <div role="row" className={`${styles.gridRem} ${styles.tableHead}`}>
                        <div role="columnheader">
                          <Tooltip title="Select all items">
                            <div className={styles.checkboxRow}>
                              <input
                                type="checkbox"
                                checked={
                                  remindersFiltered.length > 0 &&
                                  remindersFiltered.every((r) =>
                                    reminderSelected.has(r.id),
                                  )
                                }
                                onClick={(e) => e.stopPropagation()}
                                onChange={toggleReminderAll}
                              />
                              <span className={styles.checkboxLabel}></span>
                            </div>
                          </Tooltip>
                        </div>
                        <div role="columnheader">Patient name</div>
                        {sortHeader("treatmentValue", "Treatment value")}
                        <div role="columnheader">Date of Birth</div>
                        <div role="columnheader">Clinician</div>
                        <div role="columnheader">Status</div>
                        {sortHeader("nextAppointment", "Next appointment")}
                        <div role="columnheader">Email</div>
                        <div role="columnheader">Actions</div>
                      </div>

                      <div role="rowgroup" className={styles.tableBody}>
                        {loadingRem && (
                          <div className={styles.row}>
                            <div
                              role="cell" className={styles.cell}
                              style={{ gridColumn: "1 / -1" }}
                            >
                              Loading future appointmentsÃƒâ€šÃ‚Â¦
                            </div>
                          </div>
                        )}

                        {errorRem && !loadingRem && (
                          <div className={styles.row}>
                            <div
                              role="cell" className={styles.cell}
                              style={{ gridColumn: "1 / -1", color: "crimson" }}
                            >
                              {errorRem}
                            </div>
                          </div>
                        )}

                        {!loadingRem &&
                          !errorRem &&
                          remindersFiltered.length === 0 && (
                            <div className={styles.rowSoft}>
                              <div
                                role="cell" className={styles.cell}
                                style={{ gridColumn: "1 / -1", opacity: 0.7 }}
                              >
                                No data matches your filters.
                              </div>
                            </div>
                          )}

                        {!loadingRem &&
                          !errorRem &&
                          remindersFiltered.map((r) => {
                            const sel = reminderSelected.has(r.id);

                            return (
                              <div
                                role="row" className={`${styles.rowSoft} ${styles.gridRem}`}
                                key={r.id}
                                onClick={() =>
                                  openConsentBundle(r.raw?.id ?? r.id)
                                }
                                style={{ cursor: "pointer" }}
                              >
                                <div role="cell" className={styles.cell}>
                                  <input
                                    type="checkbox"
                                    checked={sel}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => toggleReminderRow(r.id)}
                                  />
                                </div>

                                <div role="cell" className={styles.cell}>
                                  <div className={styles.patient}>
                                    <span className={styles.avatar}>
                                      {r.patient
                                        .split(" ")
                                        .map((n) => n[0])
                                        .slice(0, 2)
                                        .join("")
                                        .toUpperCase()}
                                    </span>
                                    <span className={styles.patientName}>
                                      {r.patient}
                                    </span>
                                  </div>
                                </div>

                                <div role="cell" className={styles.cell}><TreatmentValuePill record={r.raw} /></div>

                                <div role="cell" className={styles.cell}>
                                  <div className={styles.patient}>
                                    <span className={styles.patientName}>
                                      {fmtSafeDate(r.date_of_birth)}
                                    </span>
                                  </div>
                                </div>

                                <div role="cell" className={styles.cell}>{r.clinician}</div>
                                <div role="cell" className={styles.cell}><ConsentStatusPill record={r.raw} /></div>
                                <div role="cell" className={styles.cell}><NextAppointmentPill record={r.raw} /></div>
                                <div role="cell" className={styles.cell}>
                                  <div className={styles.iconWithNum} tabIndex={0}
                                    title={`Emails sent: ${r.emails}; SMS sent: ${r.texts}; Reminders: ${r.reminders ?? 0}; Last reminder: ${r.lastReminder ? fmtSafeDate(r.lastReminder) : "Never"}`}>
                                    <FiMail /><span>{r.emails}</span>
                                  </div>
                                </div>

                                <div
                                  role="cell" className={`${styles.cell} ${styles.actions}`}
                                  style={{
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "center",
                                  }}
                                >
                                  {canShowQr(r.status) && (
                                    <button
                                      className={`${styles.btn} ${styles.btnLight}`}
                                      title="QR Code"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openQr(r);
                                      }}
                                    >
                                      <FiGrid />
                                    </button>
                                  )}

                                  {/* Quick View */}
                                  <QuickViewButton
                                    className={`${styles.btn} ${styles.btnLight} tutorial-quick-view`}
                                    history={histories[String(r.raw?.id ?? r.id)]}
                                    onOpen={() => openQuickView(r.raw?.id ?? r.id)}
                                  >
                                    <FiEye />
                                  </QuickViewButton>

                                  <button
                                    className={`${styles.btn} ${styles.btnLight}`}
                                    title="Download"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      downloadSingleAll(r.raw?.id ?? r.id);
                                    }}
                                  >
                                    {downloadingIds.has(r.id) ? (
                                      <span className={styles.spinner}></span>
                                    ) : (
                                      <>
                                        <FiDownload />
                                      </>
                                    )}
                                  </button>

                                  {/* Archive */}
                                  <button
                                    className={`${styles.btn} ${styles.btnLight}`}
                                    title={isArchivedRecord(r) ? "Already archived" : "Archive"}
                                    disabled={isArchivedRecord(r) || isBulkArchiving}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      askArchive(r.id, r.patient, "reminders");
                                    }}
                                  >
                                    <FiArchive />
                                  </button>

                                  <Tooltip title="Send reminder">
                                    <span>
                                      <button
                                        className={`${styles.btn} ${styles.btnOutlineBlue}`} aria-label="Send reminder"
                                        disabled={
                                          r.status?.toLowerCase() === "draft"
                                        }
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const id = Number(r.raw?.id ?? r.id);
                                          setPendingReminderIds([id]);
                                          setShowConfirmModal(true);
                                        }}
                                      >
                                        <FiSend />
                                      </button>
                                    </span>
                                  </Tooltip>
                                </div>
                              </div>
                            );
                          })}
                      </div>

                      <div className={styles.tableFooter}>
                        <div className={styles.pagerInfo}>
                          Page {page}  Per page
                          <select
                            value={perPage}
                            onChange={(e) => {
                              setPage(1);
                              setPerPage(parseInt(e.target.value, 10));
                            }}
                            style={{ marginLeft: 8 }}
                          >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                          </select>
                          <span style={{ marginLeft: 12 }}>
                            Total: {totalRem}
                          </span>
                        </div>

                        <div className={styles.pagerBtns}>
                          <button
                            className={styles.btn}
                            disabled={page <= 1 || loadingRem}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                          >
                            Prev
                          </button>

                          <button
                            className={styles.btn}
                            disabled={loadingRem || page * perPage >= totalRem}
                            onClick={() => setPage((p) => p + 1)}
                            style={{ marginLeft: 8 }}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    </div>

                  </>
                )}
                {/* ---- Ready for upload ---- */}
                {activeTab === "upload" && (
                  <>
                    <div role="table" aria-label="Consent records" className={styles.table}>
                      <div
                        role="row" className={`${styles.gridUpload} ${styles.tableHead}`}
                      >
                        <div role="columnheader">
                          <Tooltip title="Select all items">
                            <div className={styles.checkboxRow}>
                              <input
                                type="checkbox"
                                checked={
                                  uploadFiltered.length > 0 &&
                                  uploadFiltered.every((r) =>
                                    uploadSelected.has(r.id),
                                  )
                                }
                                onChange={(e) => {
                                  e.stopPropagation();
                                  toggleUploadAll();
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <span className={styles.checkboxLabel}></span>
                            </div>
                          </Tooltip>
                        </div>
                        <div role="columnheader">Patient name</div>
                        {sortHeader("treatmentValue", "Treatment value")}
                        <div role="columnheader">Date of Birth</div>
                        <div role="columnheader">Clinician</div>
                        <div role="columnheader">Status</div>
                        {sortHeader("nextAppointment", "Next appointment")}
                        <div role="columnheader">Email</div>
                        <div role="columnheader">Actions</div>
                      </div>

                      <div role="rowgroup" className={styles.tableBody}>
                        {loadingUpload && (
                          <div className={styles.row}>
                            <div
                              role="cell" className={styles.cell}
                              style={{ gridColumn: "1 / -1" }}
                            >
                              Loading ready-for-uploadÃƒâ€šÃ‚Â¦
                            </div>
                          </div>
                        )}

                        {errorUpload && !loadingUpload && (
                          <div className={styles.row}>
                            <div
                              role="cell" className={styles.cell}
                              style={{ gridColumn: "1 / -1", color: "crimson" }}
                            >
                              {errorUpload}
                            </div>
                          </div>
                        )}

                        {!loadingUpload &&
                          !errorUpload &&
                          uploadFiltered.length === 0 && (
                            <div className={styles.row}>
                              <div
                                role="cell" className={styles.cell}
                                style={{ gridColumn: "1 / -1", opacity: 0.7 }}
                              >
                                No data matches your filters.
                              </div>
                            </div>
                          )}

                        {!loadingUpload &&
                          !errorUpload &&
                          uploadFiltered.map((u) => {
                            const sel = uploadSelected.has(u.id);
                            const completed =
                              u.raw?.uploaded_to_dentally_patient_record ===
                                true ||
                              String(u.status)
                                .toLowerCase()
                                .includes("completed");

                            return (
                              <div
                                role="row" className={`${styles.gridUpload} ${
                                  completed
                                    ? styles.rowUploadCompleted
                                    : styles.rowUpload
                                }`}
                                key={u.id}
                                onClick={() =>
                                  openConsentBundle(u.raw?.id ?? u.id)
                                }
                                style={{
                                  cursor: "pointer",
                                }}
                              >
                                <div role="cell" className={styles.cell}>
                                  <input
                                    className={styles.checkboxLarge}
                                    type="checkbox"
                                    checked={sel}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      toggleUploadRow(u.id);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>

                                <div role="cell" className={styles.cell}>
                                  <div className={styles.patient}>
                                    <span className={styles.avatar}>
                                      {u.patient
                                        .split(" ")
                                        .map((n) => n[0])
                                        .slice(0, 2)
                                        .join("")
                                        .toUpperCase()}
                                    </span>
                                    <span className={styles.patientName}>
                                      {u.patient}
                                    </span>
                                  </div>
                                </div>

                                <div role="cell" className={styles.cell}><TreatmentValuePill record={u.raw} /></div>
                                <div role="cell" className={styles.cell}>{fmtSafeDate(u.raw.patient?.date_of_birth ?? u.raw.date_of_birth ?? u.raw.patient_date_of_birth)}</div>
                                <div role="cell" className={styles.cell}>{u.clinician}</div>
                                <div role="cell" className={styles.cell}><ConsentStatusPill record={u.raw} /></div>
                                <div role="cell" className={styles.cell}><NextAppointmentPill record={u.raw} /></div>
                                <div role="cell" className={styles.cell}>
                                  <div className={styles.iconWithNum} tabIndex={0}
                                    title={`Emails sent: ${u.email}; SMS sent: ${u.sms}; Signed date: ${u.signedDate ? fmtSafeDate(u.signedDate) : "Not signed"}`}>
                                    <FiMail /><span>{u.email}</span>
                                  </div>
                                </div>

                                <div
                                  role="cell" className={`${styles.cell} ${styles.actions}`}
                                  style={{
                                    display: "flex",
                                    gap: 8,
                                    alignItems: "center",
                                  }}
                                >
                                  {/*  Quick View */}
                                  <QuickViewButton
                                    className={styles.iconBtn}
                                    history={histories[String(u.raw?.id ?? u.id)]}
                                    onOpen={() => openQuickView(u.raw?.id ?? u.id)}
                                    style={{
                                      border: "none",
                                      background: "transparent",
                                      padding: 3,
                                    }}
                                  >
                                    <FiEye />
                                  </QuickViewButton>

                                  <button
                                    className={styles.iconBtn}
                                    title="Download"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      downloadSingleAll(u.raw?.id ?? u.id);
                                    }}
                                    style={{
                                      border: "none",
                                      background: "transparent",
                                      padding: 3,
                                    }}
                                  >
                                    {downloadingIds.has(u.id) ? (
                                      <span className={styles.spinner}></span>
                                    ) : (
                                      <FiDownload />
                                    )}
                                  </button>
                                  <button
                                    className={styles.iconBtn}
                                    title={isArchivedRecord(u) ? "Already archived" : "Archive"}
                                    disabled={isArchivedRecord(u) || isBulkArchiving}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      askArchive(
                                        u.raw?.id ?? u.id,
                                        u.patient,
                                        "upload",
                                      );
                                    }}
                                    style={{
                                      border: "none",
                                      background: "transparent",
                                      cursor: "pointer",
                                      padding: 6,
                                    }}
                                  >
                                    <FiArchive />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>

                    <div className={styles.tableFooter}>
                      <div className={styles.pagerInfo}>
                        Page {pageUpload}  Per page
                        <select
                          value={perPageUpload}
                          onChange={(e) => {
                            setPageUpload(1);
                            setPerPageUpload(parseInt(e.target.value, 10));
                          }}
                          style={{ marginLeft: 8 }}
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                        </select>
                        <span style={{ marginLeft: 12 }}>
                          Total: {totalUpload}
                        </span>
                      </div>
                      <div className={styles.pagerBtns}>
                        <button
                          className={styles.btn}
                          disabled={pageUpload <= 1 || loadingUpload}
                          onClick={() =>
                            setPageUpload((p) => Math.max(1, p - 1))
                          }
                        >
                          Prev
                        </button>
                        <button
                          className={styles.btn}
                          disabled={
                            loadingUpload ||
                            pageUpload * perPageUpload >= totalUpload
                          }
                          onClick={() => setPageUpload((p) => p + 1)}
                          style={{ marginLeft: 8 }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <DashboardConfirmationDialog
                open={showConfirmModal}
                kind="reminder"
                loading={isSendingReminders}
                description={
                  pendingReminderIds.length === 1
                    ? "Are you sure you want to send a reminder for this consent?"
                    : `Are you sure you want to send reminders to ${pendingReminderIds.length} selected consents?`
                }
                onCancel={() => setShowConfirmModal(false)}
                onConfirm={() => {
                  void sendReminders(pendingReminderIds);
                }}
              />
            </section>
          </>
        )}
        <footer className={styles.footerStripe} />
      </div>

      {quickViewOpen && (
        <div
          className={styles.quickViewBackdrop}
          onClick={() => setQuickViewOpen(false)}
        >
          <div
            className={styles.quickViewModal}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className={styles.quickViewTitle}>
              Interaction Log  #{quickViewId}
            </h3>

            {/* Loading */}
            {quickViewLoading && (
              <div className={styles.quickViewLoading}>LoadingÃƒâ€šÃ‚Â¦</div>
            )}

            {/* Error */}
            {quickViewError && (
              <div className={styles.quickViewError}>{quickViewError}</div>
            )}

            {/* Content */}
            {!quickViewLoading && !quickViewError && (
              <div
                className={`${styles.quickViewTimeline} tutorial-video-watch-summary`}
              >
                {Object.entries(interactionLog).map(([date, events]) => (
                  <div key={date} className={styles.timelineDay}>
                    <div className={styles.timelineDate}>{date}</div>

                    <div className={styles.timelineItems}>
                      {events.map((ev, idx) => (
                        <div key={idx} className={styles.timelineItem}>
                          <div className={styles.timelineDot}></div>

                          <div className={styles.timelineContent}>
                            <div className={styles.timelineEvent}>
                              {ev.event}
                            </div>

                            {ev.description?.length > 0 && (
                              <ul className={styles.timelineDesc}>
                                {ev.description.map((d, i) => (
                                  <li key={i}>{d}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {Object.keys(interactionLog).length === 0 && (
                  <p className={styles.timelineNoData}>
                    No interactions found.
                  </p>
                )}
              </div>
            )}

            <button
              className={styles.quickViewClose}
              onClick={() => setQuickViewOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {openingConsentBundleId && (
        <div
          className={styles.fullscreenLoader}
          role="status"
          aria-live="polite"
          aria-label="Opening consent bundle"
        >
          <div className={styles.loaderContent}>
            <Lottie
              animationData={bulkDownloadAnim}
              loop
              style={{ width: 240, height: 240 }}
            />
            <div className={styles.loaderText}>
              Opening consent bundle and checking transcription...
            </div>
            <p style={{ margin: "8px 0 0", color: "#6b7280" }}>
              A slow connection may take a little longer. Please keep this
              screen open.
            </p>
          </div>
        </div>
      )}

      {bulkDownloading && (
        <div className={styles.bulkOverlay}>
          <div className={styles.bulkOverlayContent}>
            <Lottie
              animationData={bulkDownloadAnim}
              loop
              style={{ width: 220, height: 220 }}
            />
            <p className={styles.bulkText}>Preparing your download...</p>
          </div>
        </div>
      )}

      {notify.show && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "#fff",
            padding: "24px 32px",
            borderRadius: 12,
            boxShadow: "0 12px 30px rgba(0,0,0,0.25)",
            zIndex: 99999,
            textAlign: "center",
            minWidth: 180,
          }}
        >
          {notify.type === "archive" && (
            <div
              style={{
                fontSize: 48,
                marginBottom: 8,
                animation: "binAnim 1.5s ease",
              }}
            >
             
            </div>
          )}

          {notify.type === "download" && (
            <div
              style={{
                fontSize: 40,
                marginBottom: 8,
                animation: "spin 1s linear infinite",
              }}
            >
      
            </div>
          )}

          {notify.type === "success" && (
            <div style={{ fontSize: 40, marginBottom: 8, color: "green" }}>
              
            </div>
          )}

          <div style={{ fontWeight: 600 }}>{notify.message}</div>
        </div>
      )}

      <DashboardTutorialOverlay
        open={showDashboardTutorial}
        steps={dashboardTutorialSteps}
        isPracticeVersion={isPracticeVersion}
        onClose={() => setShowDashboardTutorial(false)}
      />

      {/* QR Modal */}
      <QrModal
        open={qrOpen}
        appt={selectedAppt}
        onClose={() => setQrOpen(false)}
      />
      {confirmArchive.open && (
        <DashboardConfirmationDialog
          open={confirmArchive.open}
          kind="archive"
          loading={confirmArchive.loading}
          description={
            <>Are you sure you want to archive{confirmArchive.name ? ` ${confirmArchive.name}` : ""}?</>
          }
          onCancel={() => setConfirmArchive({ open: false, source: null, loading: false })}
          onConfirm={doArchive}
        />
      )}
    </>
  );
};

export default Dashboard;
